import "./StrgPage.css";
import React, { useEffect, useState, useRef } from "react";
import api from "../api/apiClient";
import { API_BASE } from "../api/config";
import { FaEdit, FaTrash, FaCheck, FaTimes, FaFilter, FaSync } from "react-icons/fa";

// ====== WELLE 39: EXCEL-STRG MIGRATION BUTTON ======
// Liest die Konto-Excels (Strg-Sheets) und übernimmt die Regeln in strg_config
// — Objekt+Konto-scoped, füllt nur leere Felder. Immer erst Dry-Run-Vorschau.
const MigrateExcelButton = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [fehler, setFehler] = useState(null);

  const lauf = async (dryRun) => {
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.post(`${API_BASE}/database/strg/migrate-excel-batch`,
        { dry_run: dryRun });
      setReport(r.data);
      if (!dryRun && onDone) onDone();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="sync-daten-btn" onClick={() => lauf(true)} disabled={busy}>
        {busy ? "Läuft…" : "📥 Excel-STRG migrieren"}
      </button>
      {report && (
        <div className="sync-modal-overlay" onClick={() => setReport(null)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}
               style={{ maxWidth: 720, maxHeight: "80vh", overflowY: "auto" }}>
            <h3>Excel-STRG-Migration {report.dry_run ? "(Vorschau)" : "(ausgeführt)"}</h3>
            {fehler && <p style={{ color: "#f87171" }}>{fehler}</p>}
            <p>
              {report.summen.regeln_gesamt} Regeln aus {report.dateien.length} Dateien ·{" "}
              {report.summen.konten_gesamt} Konten
              {!report.dry_run && (
                <> — <b>{report.summen.eingefuegt} neu</b>, {report.summen.aktualisiert} ergänzt,{" "}
                {report.summen.unveraendert} unverändert</>
              )}
            </p>
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr><th align="left">Datei</th><th align="left">Objekt</th>
                <th align="right">Regeln</th><th align="left">Konten</th></tr></thead>
              <tbody>
                {report.dateien.map((f, i) => (
                  <tr key={i}>
                    <td>{f.datei}{f.warnungen.map((w, j) => (
                      <div key={j} style={{ color: "#eab308", fontSize: 11 }}>⚠ {w}</div>))}</td>
                    <td>{f.objekt}</td>
                    <td align="right">{f.regeln}</td>
                    <td>{f.konten.map((k) => k.kontocode).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="sync-daten-btn" onClick={() => setReport(null)}>Schließen</button>
              {report.dry_run && (
                <button className="sync-daten-btn primary" disabled={busy} onClick={() => lauf(false)}>
                  {busy ? "Migriert…" : "Jetzt übernehmen"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ====== WELLE 46: LÜCKEN-ANSICHT ======
// Unkategorisierte Umsätze (daten.Kategorie leer) je Objekt/Konto/Partner-IBAN —
// genau das, was als STRG-Regel einzutragen ist. Regel direkt aus der Zeile anlegen.
const fmtEur = (v) =>
  (v ?? 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const LueckenAnsicht = ({ objektFilter, selectedIban, textFilter, vorschlaege, onRegelAngelegt }) => {
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [offenKey, setOffenKey] = useState(null);
  const [form, setForm] = useState({});
  const [speichert, setSpeichert] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [nurOhneRegel, setNurOhneRegel] = useState(false);

  const laden = async () => {
    setLoading(true);
    setFehler("");
    try {
      const r = await api.get(`${API_BASE}/database/strg/luecken`);
      setDaten(r.data);
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { laden(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const zeilenKey = (g) => `${g.auftragskonto}|${g.partner_iban}|${g.beguenstigter}`;
  const tf = (textFilter || "").toLowerCase();
  const zeilen = (daten?.luecken || []).filter((g) =>
    (!objektFilter || g.objekt === objektFilter) &&
    (!selectedIban || g.auftragskonto === selectedIban) &&
    (!nurOhneRegel || !g.regel_vorhanden) &&
    (!tf || (g.beguenstigter || "").toLowerCase().includes(tf) ||
      (g.partner_iban || "").toLowerCase().includes(tf) ||
      (g.beispiel_zweck || "").toLowerCase().includes(tf)));

  const objekte = (daten?.objekte || []).filter((o) => !objektFilter || o.objekt === objektFilter);

  const formOeffnen = (g) => {
    setOffenKey(zeilenKey(g));
    setForm({ KurzName: g.beguenstigter || "", Kategorie: "", SubKat: "", Kst: g.objekt || "",
              Mwst: "0,19", Umlageart: "", Gegenkonto: "" });
    setMeldung("");
    setFehler("");
  };

  const regelAnlegen = async (g) => {
    setSpeichert(true);
    setFehler("");
    try {
      await api.post(`${API_BASE}/database/strg`, {
        iban: g.auftragskonto,
        "ZahlungsempfängerIBAN": g.partner_iban,
        Beguenstigter: g.beguenstigter || "",
        KurzName: form.KurzName || "",
        Kategorie: form.Kategorie || "",
        SubKat: form.SubKat || "",
        Kst: form.Kst || "",
        Mwst: form.Mwst || "",
        Umlageart: form.Umlageart || "",
        Gegenkonto: form.Gegenkonto || "",
      });
      setOffenKey(null);
      setMeldung(`✅ Regel für „${g.beguenstigter || g.partner_iban}“ angelegt — „Regeln jetzt anwenden“ füllt die Umsätze.`);
      setDaten((d) => d ? { ...d, luecken: d.luecken.map((x) =>
        zeilenKey(x) === zeilenKey(g) ? { ...x, regel_vorhanden: true } : x) } : d);
      onRegelAngelegt && onRegelAngelegt();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    } finally {
      setSpeichert(false);
    }
  };

  const regelnAnwenden = async () => {
    setSyncing(true);
    setFehler("");
    setMeldung("");
    try {
      const params = selectedIban ? { iban: selectedIban } : {};
      const r = await api.post(`${API_BASE}/database/strg/sync-daten`, null, { params });
      const felder = r.data?.updated_fields || {};
      const gesamt = Object.values(felder).reduce((a, b) => a + (b || 0), 0);
      setMeldung(`✅ Regeln angewendet — ${gesamt} Feld-Ergänzungen (Kategorie: ${felder.Kategorie || 0}).`);
      await laden();
      onRegelAngelegt && onRegelAngelegt();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="luecken-wrap">
      <datalist id="dl-kategorien">{(vorschlaege.kategorien || []).map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="dl-subkats">{(vorschlaege.subkats || []).map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="dl-ksts">{(vorschlaege.ksts || []).map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="dl-umlagearten">{(vorschlaege.umlagearten || []).map((v) => <option key={v} value={v} />)}</datalist>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "10px 0" }}>
        <b>
          {loading ? "Lade Lücken…" :
            `${zeilen.length} Lücken-Gruppen · ${zeilen.reduce((a, g) => a + g.anzahl, 0)} Umsätze ohne Kategorie`}
        </b>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={nurOhneRegel} onChange={(e) => setNurOhneRegel(e.target.checked)} />
          nur ohne Regel
        </label>
        <span style={{ flex: 1 }} />
        <button className="sync-daten-btn" onClick={laden} disabled={loading}>↻ Neu laden</button>
        <button className="sync-daten-btn primary" onClick={regelnAnwenden} disabled={syncing}>
          {syncing ? "Wendet an…" : "🔄 Regeln jetzt anwenden"}
        </button>
      </div>

      {objekte.length > 1 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, fontSize: 12 }}>
          {objekte.map((o) => (
            <span key={o.objekt || "(ohne)"} className="badge">
              {o.objekt || "ohne Objekt"}: {o.gruppen} Gruppen · {o.umsaetze} Umsätze
            </span>
          ))}
        </div>
      )}

      {fehler && <div className="badge badge-danger" style={{ marginBottom: 8 }}>{fehler}</div>}
      {meldung && <div className="badge badge-success" style={{ marginBottom: 8 }}>{meldung}</div>}

      <table className="strg-table">
        <thead>
          <tr>
            <th>Objekt · Konto</th>
            <th>Begünstigter</th>
            <th>Partner-IBAN</th>
            <th style={{ textAlign: "right" }}>Umsätze</th>
            <th style={{ textAlign: "right" }}>Summe</th>
            <th>Letzter</th>
            <th>Beispiel-Zweck</th>
            <th className="col-actions">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((g) => (
            <React.Fragment key={zeilenKey(g)}>
              <tr style={g.regel_vorhanden ? { opacity: 0.65 } : undefined}>
                <td style={{ whiteSpace: "nowrap" }}>{[g.objekt, g.kontocode].filter(Boolean).join(" · ") || g.auftragskonto}</td>
                <td>{g.beguenstigter || <i style={{ opacity: 0.6 }}>—</i>}</td>
                <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {g.partner_iban || <i style={{ opacity: 0.6 }}>ohne IBAN</i>}
                </td>
                <td style={{ textAlign: "right" }}>{g.anzahl}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmtEur(g.summe)}</td>
                <td style={{ whiteSpace: "nowrap" }}>{g.letzter_umsatz || "—"}</td>
                <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={g.beispiel_zweck}>{g.beispiel_zweck}</td>
                <td className="col-actions">
                  {g.regel_vorhanden ? (
                    <span className="badge" title="Regel existiert schon — nur der Sync fehlt noch">Regel da</span>
                  ) : g.partner_iban ? (
                    <button className="icon-btn" title="STRG-Regel anlegen"
                            onClick={() => (offenKey === zeilenKey(g) ? setOffenKey(null) : formOeffnen(g))}>
                      ➕
                    </button>
                  ) : (
                    <span title="Ohne Partner-IBAN ist keine Regel möglich (Match läuft über die IBAN)"
                          style={{ opacity: 0.5 }}>–</span>
                  )}
                </td>
              </tr>
              {offenKey === zeilenKey(g) && (
                <tr>
                  <td colSpan={8} style={{ background: "rgba(59,130,246,0.06)" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", padding: "6px 2px" }}>
                      {[["KurzName", "KurzName", null],
                        ["Kategorie", "Kategorie", "dl-kategorien"],
                        ["SubKat", "SubKat", "dl-subkats"],
                        ["Kst", "Kst", "dl-ksts"],
                        ["Mwst", "MwSt", null],
                        ["Umlageart", "Umlageart", "dl-umlagearten"],
                        ["Gegenkonto", "Gegenkonto", null]].map(([feld, label, dl]) => (
                        <label key={feld} style={{ display: "flex", flexDirection: "column", fontSize: 11 }}>
                          {label}
                          <input
                            list={dl || undefined}
                            style={{ width: feld === "KurzName" ? 180 : 110 }}
                            value={form[feld] ?? ""}
                            onChange={(e) => setForm((f) => ({ ...f, [feld]: e.target.value }))}
                          />
                        </label>
                      ))}
                      <button className="sync-daten-btn primary" disabled={speichert} onClick={() => regelAnlegen(g)}>
                        {speichert ? "Speichert…" : "Regel speichern"}
                      </button>
                      <button className="sync-daten-btn" onClick={() => setOffenKey(null)}>Abbrechen</button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {!loading && zeilen.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: "center", opacity: 0.7, padding: 18 }}>
              🎉 Keine Lücken im aktuellen Filter — alle Umsätze haben eine Kategorie.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ====== WELLE 46: GOOGLE-SHEET-QUELLEN ======
// Öffentlicher Sheet-Link (Freigabe „Jeder mit dem Link: Betrachter") → Auto-Sync
// in strg_config. Kein API-Key, gleiche Spalten wie der Excel-Download.
const SheetSyncBereich = ({ onSynced }) => {
  const [sheets, setSheets] = useState([]);
  const [offen, setOffen] = useState(false);
  const [url, setUrl] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");

  const laden = async () => {
    try {
      const r = await api.get(`${API_BASE}/database/strg/sheets`);
      setSheets(Array.isArray(r.data) ? r.data : []);
    } catch {}
  };

  useEffect(() => {
    laden();
    // Auto-Sync beim Öffnen des Tabs — Backend nimmt nur Sheets, deren letzter
    // Sync älter als 15 Minuten ist.
    (async () => {
      try {
        const r = await api.post(`${API_BASE}/database/strg/sheet-sync`, { auto: true });
        if ((r.data?.results || []).length) {
          await laden();
          onSynced && onSynced();
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hinzufuegen = async () => {
    setFehler("");
    setMeldung("");
    try {
      await api.post(`${API_BASE}/database/strg/sheets`, { url, beschreibung });
      setUrl("");
      setBeschreibung("");
      setMeldung("Sheet-Quelle gespeichert.");
      await laden();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    }
  };

  const syncJetzt = async (sid) => {
    setBusyId(sid);
    setFehler("");
    setMeldung("");
    try {
      const r = await api.post(`${API_BASE}/database/strg/sheet-sync`, { id: sid });
      const res = (r.data?.results || [])[0];
      setMeldung(res ? res.status : "Sync ausgeführt.");
      await laden();
      onSynced && onSynced();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleAktiv = async (s) => {
    try {
      await api.put(`${API_BASE}/database/strg/sheets/${s.id}`, { aktiv: s.aktiv ? 0 : 1 });
      await laden();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    }
  };

  const loeschen = async (s) => {
    if (!window.confirm(`Sheet-Quelle „${s.beschreibung || s.url}“ wirklich entfernen?`)) return;
    try {
      await api.delete(`${API_BASE}/database/strg/sheets/${s.id}`);
      await laden();
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="card" style={{ padding: "8px 12px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
           onClick={() => setOffen(!offen)}>
        <b>🔗 Google-Sheet-Quellen ({sheets.length})</b>
        <span style={{ fontSize: 12, opacity: 0.7 }}>
          öffentlicher Link → Auto-Sync in die STRG-Tabelle
        </span>
        <span style={{ marginLeft: "auto" }}>{offen ? "▾" : "▸"}</span>
      </div>
      {offen && (
        <div style={{ marginTop: 10 }}>
          {fehler && <div className="badge badge-danger" style={{ marginBottom: 6 }}>{fehler}</div>}
          {meldung && <div className="badge badge-success" style={{ marginBottom: 6 }}>{meldung}</div>}
          {sheets.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10,
                                     padding: "4px 0", borderBottom: "1px solid rgba(128,128,128,0.15)", fontSize: 13 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4 }}
                     title="aktiv = wird beim Auto-Sync berücksichtigt">
                <input type="checkbox" checked={!!s.aktiv} onChange={() => toggleAktiv(s)} />
              </label>
              <span style={{ minWidth: 140 }}>{s.beschreibung || `Sheet ${s.id}`}</span>
              <a href={s.url} target="_blank" rel="noreferrer"
                 style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.url}
              </a>
              <span style={{ fontSize: 11, opacity: 0.75 }}>
                {s.last_sync ? `zuletzt ${s.last_sync}` : "noch nie gesynct"}
                {s.last_status ? ` — ${s.last_status}` : ""}
              </span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="sync-daten-btn" disabled={busyId === s.id} onClick={() => syncJetzt(s.id)}>
                  {busyId === s.id ? "Synct…" : "Jetzt syncen"}
                </button>
                <button className="icon-btn danger" title="Entfernen" onClick={() => loeschen(s)}><FaTrash /></button>
              </span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <input style={{ flex: 2, minWidth: 260 }} placeholder="https://docs.google.com/spreadsheets/…"
                   value={url} onChange={(e) => setUrl(e.target.value)} />
            <input style={{ flex: 1, minWidth: 140 }} placeholder="Beschreibung"
                   value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
            <button className="sync-daten-btn primary" disabled={!url.trim()} onClick={hinzufuegen}>
              ➕ Hinzufügen
            </button>
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
            Sheet-Spalten wie beim Excel-Download (id, iban, ZahlungsempfängerIBAN, KurzName, Kategorie, …).
            Freigabe: „Jeder mit dem Link – Betrachter“. Zeilen mit id aktualisieren gezielt, neue Zeilen
            brauchen iban + Partner-IBAN.
          </div>
        </div>
      )}
    </div>
  );
};

// ====== SYNC DATEN BUTTON COMPONENT ======
const SyncDatenButton = ({ selectedIban }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const params = selectedIban ? { iban: selectedIban } : {};
      const response = await api.get(`${API_BASE}/database/strg/sync-preview`, { params });
      setPreview(response.data);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const executeSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const params = selectedIban ? { iban: selectedIban } : {};
      const response = await api.post(`${API_BASE}/database/strg/sync-daten`, null, { params });
      setResult(response.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <>
      <button 
        onClick={fetchPreview} 
        disabled={loading}
        className="sync-daten-btn"
        title="Daten-Tabelle mit STRG-Konfiguration synchronisieren"
      >
        <FaSync className={loading ? 'spinning' : ''} />
        {loading ? 'Lade...' : 'Daten synchronisieren'}
      </button>

      {showModal && (
        <div className="sync-modal-overlay" onClick={closeModal}>
          <div className="sync-modal" onClick={e => e.stopPropagation()}>
            <div className="sync-modal-header">
              <h3>Daten-Synchronisation {selectedIban ? `(${selectedIban})` : '(Alle IBANs)'}</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            
            <div className="sync-modal-body">
              {error && (
                <div className="sync-error">
                  <strong>Fehler:</strong> {error}
                </div>
              )}

              {preview && !result && (
                <>
                  <div className="sync-info">
                    <p><strong>Daten-Einträge:</strong> {preview.total_daten_entries}</p>
                    <p><strong>STRG-Konfigurationen:</strong> {preview.strg_entries}</p>
                  </div>
                  
                  <h4>Leere Felder, die gefüllt werden:</h4>
                  <table className="sync-preview-table">
                    <thead>
                      <tr>
                        <th>Feld</th>
                        <th>Anzahl leer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(preview.empty_fields || {}).map(([field, count]) => (
                        <tr key={field}>
                          <td>{field.replace('_leer', '')}</td>
                          <td>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="sync-warning">
                    <strong>⚠️ Hinweis:</strong> Nur leere Felder werden gefüllt. 
                    Bestehende Werte werden NICHT überschrieben.
                  </div>
                  
                  <div className="sync-actions">
                    <button onClick={closeModal} className="secondary">Abbrechen</button>
                    <button onClick={executeSync} disabled={syncing} className="primary">
                      {syncing ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
                    </button>
                  </div>
                </>
              )}

              {result && (
                <>
                  <div className="sync-success">
                    <h4>✅ Synchronisation abgeschlossen!</h4>
                    <p><strong>Aktualisierte Einträge:</strong> {result.updated_entries}</p>
                  </div>
                  
                  {result.updated_fields && Object.keys(result.updated_fields).length > 0 && (
                    <>
                      <h4>Aktualisierte Felder:</h4>
                      <table className="sync-preview-table">
                        <thead>
                          <tr>
                            <th>Feld</th>
                            <th>Aktualisiert</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(result.updated_fields).map(([field, count]) => (
                            <tr key={field}>
                              <td>{field}</td>
                              <td>{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  
                  <div className="sync-actions">
                    <button onClick={closeModal} className="primary">Schließen</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ====== SYNC VERTRÄGE BUTTON COMPONENT ======
const SyncVertraegeButton = ({ selectedIban }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const params = selectedIban ? { iban: selectedIban } : {};
      const response = await api.get(`${API_BASE}/database/vertraege/sync-preview`, { params });
      setPreview(response.data);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const executeSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const params = selectedIban ? { iban: selectedIban } : {};
      const response = await api.post(`${API_BASE}/database/vertraege/sync-daten`, null, { params });
      setResult(response.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <>
      <button 
        onClick={fetchPreview} 
        disabled={loading}
        className="sync-vertraege-btn"
        title="Daten mit Verträgen abgleichen (Vertragsnummern in Verwendungszweck)"
      >
        <FaSync className={loading ? 'spinning' : ''} />
        {loading ? 'Lade...' : 'Verträge-Sync'}
      </button>

      {showModal && (
        <div className="sync-modal-overlay" onClick={closeModal}>
          <div className="sync-modal sync-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="sync-modal-header">
              <h3>Verträge-Synchronisation {selectedIban ? `(${selectedIban})` : '(Alle IBANs)'}</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            
            <div className="sync-modal-body">
              {error && (
                <div className="sync-error">
                  <strong>Fehler:</strong> {error}
                </div>
              )}

              {preview && !result && (
                <>
                  <div className="sync-info">
                    <p><strong>Daten-Einträge:</strong> {preview.total_daten_entries}</p>
                    <p><strong>Verträge:</strong> {preview.total_vertraege}</p>
                    <p><strong>Suchbegriffe:</strong> {preview.search_terms}</p>
                    <p><strong>🎯 Gefundene Matches:</strong> <span className="highlight">{preview.matched_entries}</span></p>
                  </div>
                  
                  <h4>Feld-Zuordnung:</h4>
                  <table className="sync-preview-table">
                    <thead>
                      <tr>
                        <th>Daten-Feld</th>
                        <th>← Vertrags-Feld</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(preview.fields_to_update || {}).map(([field, source]) => (
                        <tr key={field}>
                          <td><strong>{field}</strong></td>
                          <td>{source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {preview.sample_matches && preview.sample_matches.length > 0 && (
                    <>
                      <h4>Beispiel-Matches (erste 20):</h4>
                      <div className="sample-matches-scroll">
                        <table className="sync-preview-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Verwendungszweck</th>
                              <th>Match</th>
                              <th>Art</th>
                              <th>Lieferant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.sample_matches.map((m, i) => (
                              <tr key={i}>
                                <td>{m.daten_id}</td>
                                <td className="vzweck-cell">{m.verwendungszweck}</td>
                                <td><code>{m.matched_term}</code></td>
                                <td>{m.art}</td>
                                <td>{m.lieferant}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  
                  <div className="sync-warning warning-overwrite">
                    <strong>⚠️ Achtung:</strong> Bestehende Werte werden ÜBERSCHRIEBEN!
                    Diese Synchronisation aktualisiert alle gematchten Einträge.
                  </div>
                  
                  <div className="sync-actions">
                    <button onClick={closeModal} className="secondary">Abbrechen</button>
                    <button onClick={executeSync} disabled={syncing || preview.matched_entries === 0} className="primary">
                      {syncing ? 'Synchronisiere...' : `${preview.matched_entries} Einträge synchronisieren`}
                    </button>
                  </div>
                </>
              )}

              {result && (
                <>
                  <div className="sync-success">
                    <h4>✅ Verträge-Synchronisation abgeschlossen!</h4>
                    <p><strong>Aktualisierte Einträge:</strong> {result.updated_entries}</p>
                  </div>
                  
                  {result.updated_fields && Object.keys(result.updated_fields).length > 0 && (
                    <>
                      <h4>Aktualisierte Felder:</h4>
                      <table className="sync-preview-table">
                        <thead>
                          <tr>
                            <th>Feld</th>
                            <th>Aktualisiert</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(result.updated_fields).map(([field, count]) => (
                            <tr key={field}>
                              <td>{field}</td>
                              <td>{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  
                  <div className="sync-actions">
                    <button onClick={closeModal} className="primary">Schließen</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ====== EXCEL-STYLE FILTER COMPONENT ======
const TableFilter = ({ column, data, filters, setFilters, activeColumn, setActiveColumn }) => {
  const dropdownRef = useRef(null);
  const isOpen = activeColumn === column;
  const currentFilter = filters[column] || { search: '', selected: new Set() };
  
  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];
  
  // Get unique values for this column
  const uniqueValues = [...new Set(safeData.map(row => {
    const val = row[column];
    return val !== null && val !== undefined ? String(val) : '–';
  }))].sort();
  
  // Filter values based on search
  const filteredValues = uniqueValues.filter(v => 
    v.toLowerCase().includes((currentFilter.search || '').toLowerCase())
  );
  
  const hasActiveFilter = currentFilter.selected && currentFilter.selected.size > 0;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveColumn(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setActiveColumn]);
  
  const toggleValue = (val) => {
    const newSelected = new Set(currentFilter.selected || []);
    if (newSelected.has(val)) {
      newSelected.delete(val);
    } else {
      newSelected.add(val);
    }
    setFilters({ ...filters, [column]: { ...currentFilter, selected: newSelected } });
  };
  
  const selectAll = () => {
    setFilters({ ...filters, [column]: { ...currentFilter, selected: new Set() } });
  };
  
  const clearFilter = () => {
    setFilters({ ...filters, [column]: { search: '', selected: new Set() } });
    setActiveColumn(null);
  };
  
  return (
    <div className="filter-header" ref={dropdownRef}>
      <span>{column}</span>
      <span 
        className={`filter-icon ${hasActiveFilter ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setActiveColumn(isOpen ? null : column);
        }}
      >
        <FaFilter size={10} />
        {hasActiveFilter && <span className="filter-badge">{currentFilter.selected.size}</span>}
      </span>
      
      {isOpen && (
        <div className="filter-dropdown" onClick={e => e.stopPropagation()}>
          <input
            type="text"
            className="filter-dropdown-search"
            placeholder="Suchen..."
            value={currentFilter.search || ''}
            onChange={(e) => setFilters({ 
              ...filters, 
              [column]: { ...currentFilter, search: e.target.value } 
            })}
            autoFocus
          />
          <div className="filter-options">
            {filteredValues.map(val => (
              <label key={val} className="filter-option">
                <input
                  type="checkbox"
                  checked={!currentFilter.selected || currentFilter.selected.size === 0 || currentFilter.selected.has(val)}
                  onChange={() => toggleValue(val)}
                />
                <span>{val}</span>
              </label>
            ))}
          </div>
          <div className="filter-actions">
            <button className="secondary" onClick={selectAll}>Alle</button>
            <button onClick={clearFilter}>Filter löschen</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to apply filters to data
const applyTableFilters = (data, filters) => {
  if (!filters || Object.keys(filters).length === 0) return data;
  
  return data.filter(row => {
    return Object.entries(filters).every(([col, filter]) => {
      if (!filter.selected || filter.selected.size === 0) return true;
      const val = row[col] !== null && row[col] !== undefined ? String(row[col]) : '–';
      return filter.selected.has(val);
    });
  });
};

const StrgPage = () => {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedIban, setSelectedIban] = useState("");
  // Welle 39: Objekt-Filter vor der Konto-Auswahl (Regeln sind Objekt+Konto-scoped)
  const [objektFilter, setObjektFilter] = useState("");
  const [newEntry, setNewEntry] = useState({
    Beguenstigter: "",
    ZahlungsempfängerIBAN: "",
    KurzName: "",
    Kategorie: "",
    SubKat: "",
    Kst: "",
    Mwst: "",
    Umlageart: "",
    BelegID: "",
    Gegenkonto: "",
    iban: "",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  // Welle 46: Regeln|Lücken-Umschalter, STRG-Meldungen, Datei-Upload
  const [strgView, setStrgView] = useState("regeln");
  const [strgMsg, setStrgMsg] = useState("");
  const [strgErr, setStrgErr] = useState("");
  const strgFileRef = useRef(null);

  const strgToast = (msg, err) => {
    setStrgMsg(err ? "" : msg);
    setStrgErr(err ? msg : "");
    setTimeout(() => { setStrgMsg(""); setStrgErr(""); }, 6000);
  };

  const [viewMode, setViewMode] = useState("RENAME");
  const [renameCategory, setRenameCategory] = useState("person");
  const [renameEntries, setRenameEntries] = useState([]);
  const [renameEditIndex, setRenameEditIndex] = useState(null);
  const [renameNewOpen, setRenameNewOpen] = useState(false);
  const [renameNewEntry, setRenameNewEntry] = useState({
    bezeichnung: "", kuerzel: "", schema: "", person: "", priority: 100, table: "person",
    // IBAN-spezifisch
    iban: "", bank: "", objekt: "", schema_code: "",
    // Mandat-spezifisch
    str_nr: "", mandanten_nr: "",
  });
  // Trigger for reloading entries (esp. in "Alle Einträge")
  const [renameReloadNonce, setRenameReloadNonce] = useState(0);
  const [renameCategories, setRenameCategories] = useState([]);
  const [, setRenameCatLoading] = useState(false);
  const [renameFormError, setRenameFormError] = useState("");

  // Fallback: falls Backend-Kategorien leer/fehlerhaft sind
  const DEFAULT_EMPFAENGER_CATEGORIES = [
    { code: "person", label: "Personen", count: null },
    { code: "objekt", label: "Objekte", count: null },
    { code: "vertrag", label: "Verträge", count: null },
    { code: "iban", label: "IBAN", count: null },
    { code: "absender", label: "Absender", count: null },
    { code: "mandat", label: "Mandant", count: null },
    { code: "versicherung", label: "Versicherung", count: null },
    { code: "steuernummer", label: "Steuernummer", count: null },
  ];

  const TABLE_OPTIONS = [
    { code: "person", label: "Person" },
    { code: "objekt", label: "Objekt" },
    { code: "vertrag", label: "Vertrag" },
    { code: "iban", label: "IBAN" },
    { code: "extra", label: "Extra" },
    { code: "absender", label: "Absender" },
    { code: "mandat", label: "Mandant" },
    { code: "versicherung", label: "Versicherung" },
    { code: "steuernummer", label: "Steuernummer" },
  ];
const tableLabel = (code) => (TABLE_OPTIONS.find(t => t.code === code)?.label || code || "–");
const TABLE_CODES = TABLE_OPTIONS.map(t => t.code);
const isObjRow = (row) => (row.table || row._originalTable) === "objekt";

const getDoktypValue = (row) => {
  const t = row.table || row._originalTable;
  if (t === "iban") return row.schema_code || "–";
  if (t === "mandat") return row.schema_code || "–";
  if (t === "objekt") return row.person || "–";
  return row.schema || "–";
};

  // Restore persisted view state
  useEffect(() => {
    try {
      const vm = localStorage.getItem("strg.viewMode");
      if (vm) setViewMode(vm);
      const rc = localStorage.getItem("strg.renameCategory");
      if (rc && rc !== "all" && rc !== "extra") setRenameCategory(rc);
      const ss = localStorage.getItem("strg.schemaSelected");
      if (ss) setSchemaSelected(ss);
    } catch {}
  }, []);

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (viewMode !== "RENAME") return;
    const next = TABLE_CODES.includes(renameCategory) ? renameCategory : "person";
    setRenameNewEntry(prev => ({ ...prev, table: next }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameCategory, viewMode]);

  // Persist view state
  useEffect(() => { try { localStorage.setItem("strg.viewMode", viewMode); } catch {} }, [viewMode]);
  useEffect(() => { try { localStorage.setItem("strg.renameCategory", renameCategory); } catch {} }, [renameCategory]);

  // ==== SCHEMA-KEYWORDS: State ====
  const [schemaSchemas, setSchemaSchemas] = useState([]); // Liste der Schemas (mit Counts)
  const [schemaSelected, setSchemaSelected] = useState("");
  const [schemaKeywords, setSchemaKeywords] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaEditIndex, setSchemaEditIndex] = useState(null);
  const [schemaNewOpen, setSchemaNewOpen] = useState(false);
  const [schemaNewCode, setSchemaNewCode] = useState("");
  const [schemaNewEntry, setSchemaNewEntry] = useState({ token: "", match_mode: "substr", priority: 100, enabled: 1, erweiterung: "" });
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchStatus, setBatchStatus] = useState("");
  const [batchError, setBatchError] = useState("");

  // ==== COLLAPSIBLE CATEGORIES ====
  const [schemaCatsExpanded, setSchemaCatsExpanded] = useState(false);
  const SCHEMA_VISIBLE_COUNT = 8; // Anzahl der sichtbar angezeigten Doktypen

  // ==== TABLE FILTERS ====
  const [keywordFilters, setKeywordFilters] = useState({});
  const [keywordFilterCol, setKeywordFilterCol] = useState(null);
  const [renameFilters, setRenameFilters] = useState({});
  const [renameFilterCol, setRenameFilterCol] = useState(null);
  // Filter-State für spätere Nutzung vorbereitet
  // const [specialFilters, setSpecialFilters] = useState({});
  // const [specialFilterCol, setSpecialFilterCol] = useState(null);
  // const [strgFilters, setStrgFilters] = useState({});
  // const [strgFilterCol, setStrgFilterCol] = useState(null);

  // ==== FILENAME TEMPLATES (Doktyp-abhängig) ====
  const [, setTplLegend] = useState({});
  const [tplMap, setTplMap] = useState({});
  const [tplOpen, setTplOpen] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplMsg, setTplMsg] = useState("");
  const [tplErr, setTplErr] = useState("");

  // Persist selected schema (must be after schemaSelected is declared)
  useEffect(() => {
    if (!schemaSelected) return;
    try { localStorage.setItem("strg.schemaSelected", schemaSelected); } catch {}
  }, [schemaSelected]);

  // --- helpers to normalize backend responses (support old/new shapes) ---
  // --- Normalize invoice placeholder for templates (frontend display & edit) ---
  const normalizeInv6 = (s) => (String(s || "").replaceAll("{invoice_no}", "[INV6]").replaceAll("{INV6}", "[INV6]"));
  // Force bracket-style placeholders and canonical upper-case names
  const toBracketTemplate = (s) => {
    let out = String(s || "");
    // Curly to brackets
    out = out.replaceAll("{prefix}", "[PREFIX]")
             .replaceAll("{date}", "[YYYY-MM-DD]")
             .replaceAll("{sender}", "[SENDER]")
             .replaceAll("{account}", "[ACCOUNT]")
             .replaceAll("{statement}", "[STATEMENT]")
             .replaceAll("{invoice_no}", "[INV6]")
             .replaceAll("{employee}", "[EMPLOYEE]")
             .replaceAll("{schema}", "[SCHEMA]")
             .replaceAll("{year}", "[YEAR]")
             .replaceAll("{m}", "[M]")
             .replaceAll("{mandant}", "[MANDANT]")
             .replaceAll("{bwa_variant}", "[BWA_VARIANT]")
             .replaceAll("{vnr5}", "[VNR5]")
             .replaceAll("{doktyp}", "[DOKTYP]");
    // Lower-case bracket aliases to upper-case
    out = out.replaceAll("[prefix]", "[PREFIX]")
             .replaceAll("[date]", "[YYYY-MM-DD]")
             .replaceAll("[sender]", "[SENDER]")
             .replaceAll("[account]", "[ACCOUNT]")
             .replaceAll("[statement]", "[STATEMENT]")
             .replaceAll("[invoice_no]", "[INV6]")
             .replaceAll("[inv6]", "[INV6]")
             .replaceAll("[schema]", "[SCHEMA]")
             .replaceAll("[doktyp]", "[DOKTYP]")
             .replaceAll("[year]", "[YEAR]")
             .replaceAll("[m]", "[M]")
             .replaceAll("[mandant]", "[MANDANT]")
             .replaceAll("[bwa_variant]", "[BWA_VARIANT]")
             .replaceAll("[vnr5]", "[VNR5]");
    return out;
  };
  const normalizeSchemasResponse = (data) => {
    // Convert to [{ schema_code, count }] and DEDUPE by lowercased code
    const pushUnique = (arr, code, count = null) => {
      const key = String(code || "").trim();
      if (!key) return;
      const low = key.toLowerCase();
      if (arr.some((x) => (x.schema_code || "").toLowerCase() === low)) return;
      arr.push({ schema_code: key, count: typeof count === "number" ? count : null });
    };

    const out = [];
    if (Array.isArray(data)) {
      for (const item of data) {
        if (typeof item === "string") pushUnique(out, item, null);
        else if (item && typeof item === "object" && item.schema_code) pushUnique(out, item.schema_code, item.count);
      }
      return out;
    }
    if (data && Array.isArray(data.schemas)) {
      for (const item of data.schemas) {
        if (typeof item === "string") pushUnique(out, item, null);
        else if (item && typeof item === "object" && item.schema_code) pushUnique(out, item.schema_code, item.count);
      }
      return out;
    }
    return out;
  };
  const normalizeKeywordsResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  };

  // ---- Filename template helpers ----
  const LOCAL_TEMPLATE_FALLBACK = "[PREFIX] - [YYYY-MM-DD] - [SENDER].pdf";

  const fetchTplLegend = async () => {
    try {
      const r = await api.get(`${API_BASE}/rename-me-ocr/api/filename_template_legend`);
      return r?.data || {};
    } catch (e) {
      // Fallback: local legend
      return {
        "[PREFIX]": "Ermittelter Präfix (z. B. Objekt-Kürzel oder Person)",
        "[YYYY-MM-DD]": "Datum im ISO-Format",
        "[YYYY-MM]": "Jahr-Monat",
        "[YEAR]": "Jahr (z. B. 2025)",
        "[M]": "Monat (ohne führende Null)",
        "[SENDER]": "Normalisierter Absender",
        "[AUSZUG]": "Kontoauszugs-Nummer/-Bezeichnung",
        "[INV6]": "Letzte 6 Ziffern der Rechnungsnummer",
        "[VNR5]": "Vertragsnummer – letzte 5 Zeichen",
        "[SCHEMA]": "Erkannter Doktyp",
        "[DOKTYP]": "Alias für [SCHEMA]",
        "[MANDANT]": "Mandanten-Nr. (z. B. 31113)",
        "[BWA_VARIANT]": "BWA-Variante (z. B. Gesamt / UstVA / konsolidiert)",
      };
    }
  };

  const fetchTplMap = async () => {
    // Try canonical, then fallbacks. Tolerate 404/connection errors.
    const endpoints = [
      `${API_BASE}/database/filename_templates`, // DB first (authoritative)
      `${API_BASE}/rename-me-ocr/api/filename_templates`, // runtime/merged fallback
      `${API_BASE}/renameme/filename_templates`, // legacy fallback
    ];
    for (const url of endpoints) {
      try {
        const r = await api.get(url);
        if (r && r.data) return r.data;
      } catch (_) { /* ignore and try next */ }
    }
    return {};
  };

  const saveTplMap = async (map) => {
    const payload = { filename_templates: map };
    const endpoints = [
      `${API_BASE}/database/filename_templates`, // write to DB
      `${API_BASE}/rename-me-ocr/api/filename_templates`, // fallback
      `${API_BASE}/renameme/filename_templates`, // legacy fallback
    ];
    for (const url of endpoints) {
      try {
        await api.put(url, payload);
        return true;
      } catch (_) { /* try next */ }
    }
    return false;
  };

  const effectiveTplFor = (code) => {
    const c = String(code || "").trim().toLowerCase();
    return (tplMap?.[c]) || tplMap?.fallback || LOCAL_TEMPLATE_FALLBACK;
  };

  const uiTplValue = (code) => {
    const c = String(code || "").trim().toLowerCase();
    const explicit = (tplMap?.[c] ?? "");
    if (String(explicit).trim().length > 0) return explicit;
    // show the effective value (fallback or backend default) when no explicit value stored
    return effectiveTplFor(code);
  };

  // Check if the currently selected Doktyp has at least one keyword in the current list
  const canSaveTpl = (code) => {
    const sel = String(code || schemaSelected || "").trim();
    if (!sel) return false;
    const list = Array.isArray(schemaKeywords) ? schemaKeywords : [];
    return list.some(k => (k?.schema_code || sel) === sel);
  };

  const samplePreview = (tpl, doktyp) => {
    try {
      const date = new Date().toISOString().slice(0, 10);
      // Sample values to mirror backend fields for previews
      const valuesBracket = {
        PREFIX: "ABC",
        "YYYY-MM-DD": date,
        "YYYY-MM": date.slice(0, 7),
        YEAR: date.slice(0, 4),
        M: String(new Date().getMonth() + 1),
        SENDER: "Muster GmbH",
        AUSZUG: "Auszug 2025-10",
        INV6: "102025",
        VNR5: "94609",
        SCHEMA: doktyp || "",
        DOKTYP: doktyp || "",
        ACCOUNT: "BBB-KKK",
        STATEMENT: "Auszug 2025-10",
        SUBJECT: "Betreff",
        PERIOD: "2025-10",
        MANDANT: "31113",
        BWA_VARIANT: "Gesamt",
      };
      
      const valuesCurly = {
        prefix: valuesBracket.PREFIX,
        date: valuesBracket["YYYY-MM-DD"],
        year: valuesBracket.YEAR,
        m: valuesBracket.M,
        sender: valuesBracket.SENDER,
        account: valuesBracket.ACCOUNT,
        statement: valuesBracket.STATEMENT,
        invoice_no: "RG102025",
        employee: "Max Muster",
        schema: valuesBracket.SCHEMA,
        doktyp: valuesBracket.DOKTYP,
        mandant: valuesBracket.MANDANT,
        bwa_variant: valuesBracket.BWA_VARIANT,
        vnr5: valuesBracket.VNR5,
      };
  
      let out = String(tpl || LOCAL_TEMPLATE_FALLBACK);
      // Replace [PLACEHOLDER]
      Object.entries(valuesBracket).forEach(([k, v]) => {
        out = out.replaceAll(`[${k}]`, String(v));
      });
      // Replace {placeholder}
      Object.entries(valuesCurly).forEach(([k, v]) => {
        out = out.replaceAll(`{${k}}`, String(v));
      });
  
      // Tidy up and ensure extension
      out = out.replace(/\s{2,}/g, " ").replace(/\s-\s-\s/g, " - ").trim();
      if (!/\.pdf$/i.test(out)) out += ".pdf";
      return out;
    } catch {
      return tpl || LOCAL_TEMPLATE_FALLBACK;
    }
  };
  // Load filename template legend and templates when entering SCHEMA view
  useEffect(() => {
    if (viewMode !== "SCHEMA") return;
    let cancelled = false;
    (async () => {
      try {
        setTplLoading(true);
        const [legend, map] = await Promise.all([fetchTplLegend(), fetchTplMap()]);
        if (!cancelled) {
          setTplLegend(legend || {});
          // Normalize keys to lower-case and values to bracket/uppercase placeholders for UI
          const norm = {};
          Object.entries(map || {}).forEach(([k, v]) => {
            if (!k) return;
            const key = String(k).toLowerCase();
            const val = toBracketTemplate(normalizeInv6(String(v || "")));
            norm[key] = val;
          });
          setTplMap(norm);
        }
      } finally {
        if (!cancelled) setTplLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode]);

  // Handlers for editing/saving/resetting filename template for selected Doktyp
  const handleTplChange = (code, value) => {
    const key = String(code || "").toLowerCase();
    const val = toBracketTemplate(normalizeInv6(value));
    setTplMap(prev => ({ ...(prev || {}), [key]: val }));
  };

  // Ref für Template-Input (zum Einfügen von Platzhaltern)
  const tplInputRef = useRef(null);

  // ALLE verfügbaren Platzhalter mit deutschen Namen und Beschreibungen
  const ALL_PLACEHOLDERS = [
    // Basis-Platzhalter
    { code: "[PREFIX]", label: "Präfix", desc: "Ermittelter Präfix (z. B. Objekt-Kürzel oder Person)", category: "basis" },
    { code: "[SENDER]", label: "Absender", desc: "Normalisierter Absender/Firma", category: "basis" },
    { code: "[SCHEMA]", label: "Doktyp", desc: "Erkannter Dokumenttyp", category: "basis" },
    { code: "[DOKTYP]", label: "Doktyp (Alias)", desc: "Alias für Schema", category: "basis" },
    
    // Datum-Platzhalter
    { code: "[YYYY-MM-DD]", label: "Datum", desc: "Datum im ISO-Format (z. B. 2025-12-03)", category: "datum" },
    { code: "[YYYY-MM]", label: "Jahr-Monat", desc: "Jahr und Monat (z. B. 2025-12)", category: "datum" },
    { code: "[YEAR]", label: "Jahr", desc: "Nur das Jahr (z. B. 2025)", category: "datum" },
    { code: "[M]", label: "Monat", desc: "Monat ohne führende Null (1-12)", category: "datum" },
    
    // Rechnungs-Platzhalter
    { code: "[INV6]", label: "Rechnungsnr.", desc: "Letzte 6 Ziffern der Rechnungsnummer", category: "rechnung" },
    { code: "[VNR5]", label: "Vertragsnr.", desc: "Vertragsnummer – letzte 5 Zeichen", category: "rechnung" },
    { code: "[DNR5]", label: "Darlehensnr.", desc: "D + letzte 5 Ziffern der Darlehensnummer (z.B. D76563) – optional, nur wenn gefunden", category: "rechnung" },
    
    // Konto-Platzhalter
    { code: "[ACCOUNT]", label: "Konto", desc: "Konto-/Bank-Kennung (z. B. BBB-KKK)", category: "konto" },
    { code: "[STATEMENT]", label: "Auszug", desc: "Kontoauszugs-Bezeichnung", category: "konto" },
    { code: "[AUSZUG]", label: "Auszug (Alias)", desc: "Alias für Statement", category: "konto" },
    
    // BWA/Mandant-Platzhalter
    { code: "[MANDANT]", label: "Mandant", desc: "Mandanten-Nr. (z. B. 31113)", category: "bwa" },
    { code: "[BWA_VARIANT]", label: "BWA-Variante", desc: "BWA-Typ (z. B. Gesamt, UstVA)", category: "bwa" },
    
    // Sonstige
    { code: "[EMPLOYEE]", label: "Mitarbeiter", desc: "Mitarbeitername", category: "sonstige" },
    { code: "[SUBJECT]", label: "Betreff", desc: "Dokumentbetreff", category: "sonstige" },
    { code: "[PERIOD]", label: "Zeitraum", desc: "Abrechnungszeitraum", category: "sonstige" },
    { code: "[LEISTUNGSZEITRAUM]", label: "Leistungszeitraum", desc: "Leistungszeitraum in Klammern (nur wenn gefunden, z.B. 2025-Q4)", category: "sonstige" },
  ];

  // Kategorien für die Gruppierung
  const PLACEHOLDER_CATEGORIES = {
    basis: { label: "Basis", color: "#8b5cf6" },
    datum: { label: "Datum", color: "#3b82f6" },
    rechnung: { label: "Rechnung", color: "#10b981" },
    konto: { label: "Konto", color: "#f59e0b" },
    bwa: { label: "BWA/Mandant", color: "#ef4444" },
    sonstige: { label: "Sonstige", color: "#6b7280" },
  };

  // Platzhalter ins Template einfügen (an Cursor-Position oder am Ende)
  const insertPlaceholder = (placeholder) => {
    const input = tplInputRef.current;
    if (!input) return;
    
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = uiTplValue(schemaSelected) || "";
    
    // Füge Platzhalter an Cursor-Position ein
    const newValue = currentValue.slice(0, start) + placeholder + currentValue.slice(end);
    handleTplChange(schemaSelected, newValue);
    
    // Setze Cursor nach dem eingefügten Platzhalter
    setTimeout(() => {
      if (input) {
        input.focus();
        const newPos = start + placeholder.length;
        input.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  const handleTplReset = (code) => {
    const key = String(code || "").toLowerCase();
    setTplMap(prev => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  };

  const handleTplSave = async () => {
    try {
      setTplSaving(true); setTplMsg(""); setTplErr("");

      // Validation: require at least one keyword for the selected Doktyp
      const sel = String(schemaSelected || "").trim();
      const list = Array.isArray(schemaKeywords) ? schemaKeywords : [];
      const hasKw = !!sel && list.some(k => (k?.schema_code || sel) === sel);

      if (!sel) {
        setTplErr("Bitte zuerst einen Doktyp auswählen.");
        return;
      }
      if (!hasKw) {
        setTplErr("Für diesen Doktyp existiert noch kein Schlüsselwort. Bitte lege zuerst mindestens ein Keyword an.");
        // Öffne die Inline-Zeile zum schnellen Anlegen
        setSchemaNewOpen(true);
        return;
      }

      const ok = await saveTplMap(tplMap);
      if (ok) setTplMsg("Vorlage gespeichert.");
      else setTplErr("Speichern nicht möglich (Endpoint fehlt?).");
    } finally {
      setTplSaving(false);
      setTimeout(() => { setTplMsg(""); setTplErr(""); }, 3000);
    }
  };

  // Add a Doktyp locally if not already present (case-insensitive, top of list)
  const addSchemaIfMissing = (code) => {
    const next = String(code || "").trim();
    if (!next) return;
    setSchemaSchemas((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const low = next.toLowerCase();
      if (!arr.some((x) => (typeof x === "string" ? x : x?.schema_code)?.toLowerCase() === low)) {
        arr.unshift({ schema_code: next, count: null });
      }
      return arr;
    });
  };

  // ==== SPECIAL RULES: State (SIMPLE SCHEMA) ====
  const [specialRules, setSpecialRules] = useState([]);
  const [, setSpecialLoading] = useState(false);
  const [specialEditIndex, setSpecialEditIndex] = useState(null);
  const [specialNewOpen, setSpecialNewOpen] = useState(false);

  const DE_SCOPE_SIMPLE = {
    global: "Alle Dokumente",
    absender: "Absender",
    empfaenger_person: "Empfänger · Personen",
    empfaenger_objekt: "Empfänger · Objekte",
    empfaenger_vertrag: "Empfänger · Verträge",
    empfaenger_iban: "Empfänger · IBAN",
    empfaenger_extra: "Empfänger · Extra",
    mandat: "Mandant",
    versicherung: "Versicherung",
    steuernummer: "Steuernummer",
    doktyp: "Dokumenttyp",
    erweiterung: "Erweiterung (Dateiname)",
    keyword: "Keyword (im Text)",
  };
  const OPT_SCOPE_SIMPLE = Object.entries(DE_SCOPE_SIMPLE).map(([value, label]) => ({ value, label }));

  const DE_THEN_FIELD = {
    rechnungsnummer: "Rechnungsnummer",
    datum: "Datum",
    prefix: "Präfix",
    sender: "Absender",
    zusatz: "Zusatz (Dateiname)",
    custom: "Benutzerfeld (intern)",
    mandant: "Mandant",
    bwa_variant: "BWA-Variante",
    vnr5: "Vertragsnummer (letzte 5)",
    doktyp: "Dokumenttyp",
    leistungszeitraum: "Leistungszeitraum",
    auszug_nr: "Auszugsnummer [YEAR]-[N]",
  };

  // Verfügbare Schemata für DokTyp-Override
  const SCHEMA_OPTIONS = [
    { value: "rechnung", label: "Rechnung" },
    { value: "mahnung", label: "Mahnung" },
    { value: "lieferschein", label: "Lieferschein" },
    { value: "kontoauszug", label: "Kontoauszug" },
    { value: "bwa", label: "BWA" },
    { value: "gehaltsabrechnung", label: "Gehaltsabrechnung" },
    { value: "versorger", label: "Versorger" },
    { value: "nebenkostenabrechnung", label: "Nebenkostenabrechnung" },
    { value: "versicherung", label: "Versicherung" },
    { value: "finanzamt", label: "Finanzamt" },
    { value: "turnierabrechnung", label: "Turnierabrechnung" },
    { value: "sepa-le", label: "SEPA-LE" },
  ];
  const normalizeThenFieldValue = (v) => (String(v) === "empfaenger" ? "prefix" : v);
  const OPT_THEN_FIELD = Object.entries(DE_THEN_FIELD).map(([value, label]) => ({ value, label }));

  const DE_WRITE_MODE = { replace: "Ersetzen", append: "Anhängen" };
  const OPT_WRITE_MODE = Object.entries(DE_WRITE_MODE).map(([value, label]) => ({ value, label }));

  const DE_SEARCH_MODE = { token: "Token suchen", neu: "Neu" };
  const OPT_SEARCH_MODE = Object.entries(DE_SEARCH_MODE).map(([value, label]) => ({ value, label }));

  const DE_POS = { gleich: "Gleich", links: "Links", rechts: "Rechts", oben: "Oben", unten: "Unten" };
  const OPT_POS = Object.entries(DE_POS).map(([value, label]) => ({ value, label }));

  const specialDefault = {
    aktiv: 1,
    when_scope: "global",
    kuerzel: "",
    then_field: "rechnungsnummer",
    write_mode: "replace",
    search_mode: "token",
    search_token: "",
    pos: "gleich",
    key_mask: "",
    priority: 100,
    notes: "",
  };
  const [specialNew, setSpecialNew] = useState({ ...specialDefault });

  // ==== SPECIAL RULES: fetch with 404-retry ====
  const fetchSpecialRulesWithRetry = async (maxRetries = 1, delayMs = 200) => {
    let lastErr;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const rRes = await api.get(`${API_BASE}/database/special_rules`);
        return Array.isArray(rRes.data) ? rRes.data : [];
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        if (status === 404 && attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  };

  // normalize backend values like 0/1/2/3 or strings to our 4 canonical strings
  const normalizeMatchMode = (v) => {
    if (v === null || v === undefined) return "substr";
    if (typeof v === "number") return ["substr", "word", "nummer", "zeit"][v] ?? "substr";
    const s = String(v).toLowerCase();
    if (/^(0|1|2|3)$/.test(s)) return ["substr", "word", "nummer", "zeit"][parseInt(s, 10)] ?? "substr";
    const map = { substr: "substr", substring: "substr", word: "word", w: "word", nummer: "nummer", number: "nummer", num: "nummer", zeit: "zeit", time: "zeit" };
    return map[s] ?? "substr";
  };

  // encode canonical match_mode to backend numeric (0:substr,1:word,2:nummer,3:zeit)
  const encodeMatchMode = (v) => {
    const s = normalizeMatchMode(v);
    const map = { substr: 0, word: 1, nummer: 2, zeit: 3 };
    return map[s] ?? 0;
  };

  useEffect(() => {
    if (viewMode !== "RENAME") return;

    let cancelled = false;
    (async () => {
      // Capture current category for async guard
      const currentCategory = renameCategory;
      // 1) Kategorien laden: Empfänger + Absender zusammenführen
      try {
        setRenameCatLoading(true);
        let cats = [];
        try {
          const emp = await api.get(`${API_BASE}/database/empfaenger/categories`);
          if (Array.isArray(emp.data)) cats = cats.concat(emp.data);
        } catch (e) {
          console.warn("Empfänger-Kategorien fehlgeschlagen:", e);
        }
        try {
          const abs = await api.get(`${API_BASE}/database/absender/categories`);
          if (Array.isArray(abs.data)) cats = cats.concat(abs.data);
        } catch (e) {
          console.warn("Absender-Kategorien fehlgeschlagen:", e);
        }
        // Falls leer: Fallback auf Default-Liste
        if (!cats || cats.length === 0) cats = DEFAULT_EMPFAENGER_CATEGORIES;

        // Deduplizieren nach code
        const byCode = new Map();
        for (const c of cats) {
          if (c && c.code) byCode.set(c.code, c);
        }
        const merged = Array.from(byCode.values());
        // "all" und "extra" aus der Tab-Liste entfernen
        const filteredMerged = merged.filter(c => c.code !== "all" && c.code !== "extra");
        merged.splice(0, merged.length, ...filteredMerged);

        if (!cancelled) {
          // Mandant-Tab ergänzen, falls Backend ihn (noch) nicht liefert
          if (!merged.some(c => c.code === "mandat")) {
            merged.push({ code: "mandat", label: "Mandant", count: null });
          }
          setRenameCategories(merged);
          const codes = new Set(merged.map((c) => c.code));
          if (!codes.has(renameCategory) && merged.length > 0) {
            setRenameCategory("person");
            return; // nächster Lauf lädt die Einträge
          }
        }
      } catch (err) {
        console.error("Fehler beim Laden der Kategorien:", err);
        if (!cancelled) {
          setRenameCategories(DEFAULT_EMPFAENGER_CATEGORIES);
          if (!renameCategory) setRenameCategory("person");
        }
      } finally {
        if (!cancelled) setRenameCatLoading(false);
      }

      // 2) Einträge für aktuelle Kategorie laden
      // Clear list to avoid mixed results while switching filters
      if (!cancelled) setRenameEntries([]);
      try {
        let arr = [];
        if (renameCategory === "absender") {
          const listRes = await api.get(`${API_BASE}/database/absender`);
          arr = Array.isArray(listRes.data) ? listRes.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : [];
        } else if (renameCategory === "all") {
          const outMap = new Map();
          const pushList = (list, tableCode) => {
            (Array.isArray(list) ? list : []).forEach(x => {
              const key = (x && x.id !== undefined && x.id !== null)
  ? `${tableCode}:${x.id}`
  : `${tableCode}:${x.bezeichnung}:${x.kuerzel}:${x.schema}`;
              if (!outMap.has(key)) outMap.set(key, { ...x, table: tableCode, _originalTable: tableCode });
            });
          };

          // Empfänger zuerst: person, objekt, vertrag (Priorität sichert korrekte Tabelle für neue Einträge)
          // IBAN absichtlich NICHT in "all" aufnehmen; IBAN hat eigene Ansicht
          for (const cat of ["person", "objekt", "vertrag", /* "iban", */ "extra"]) {
            try {
              const r = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: cat } });
              pushList(r.data, cat);
            } catch (e) { console.warn(`Empfänger-Liste ${cat} fehlgeschlagen`, e); }
          }
          // Dann Absender
          try {
            const a = await api.get(`${API_BASE}/database/absender`);
            pushList(a.data, "absender");
          } catch (e) { console.warn("Absender-Liste fehlgeschlagen", e); }

          arr = Array.from(outMap.values());
        } else if (renameCategory === "mandat") {
          const listRes = await api.get(`${API_BASE}/database/empfaenger_mandat`);
          arr = Array.isArray(listRes.data)
            ? listRes.data.map(x => ({ ...x, table: "mandat", _originalTable: "mandat" }))
            : [];
        } else if (renameCategory === "versicherung") {
          const listRes = await api.get(`${API_BASE}/database/versicherungen`);
          arr = Array.isArray(listRes.data)
            ? listRes.data.map(x => ({ ...x, table: "versicherung", _originalTable: "versicherung" }))
            : [];
        } else if (renameCategory === "steuernummer") {
          const listRes = await api.get(`${API_BASE}/database/steuernummer`);
          arr = Array.isArray(listRes.data)
            ? listRes.data.map(x => ({ ...x, table: "steuernummer", _originalTable: "steuernummer" }))
            : [];
        } else if (renameCategory === "vertrag") {
          const listRes = await api.get(`${API_BASE}/database/vertraege`);
          arr = Array.isArray(listRes.data?.items)
            ? listRes.data.items.map(x => ({ ...x, table: "vertrag", _originalTable: "vertrag" }))
            : [];
        } else {
          // Einzelne Empfänger-Kategorie
          const listRes = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
          arr = Array.isArray(listRes.data) ? listRes.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : [];
        }
        if (!cancelled && currentCategory === renameCategory) setRenameEntries(arr);
      } catch (err) {
        console.error("Fehler beim Laden der Einträge:", err);
        if (!cancelled) setRenameEntries([]);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, renameCategory, renameReloadNonce]);

  // Ensure newly typed schema codes show up in the segmented bar immediately
  useEffect(() => {
    if (!schemaSelected) return;
    const exists = (schemaSchemas || []).some((s) => (
      typeof s === "string" ? s : s?.schema_code
    ) === schemaSelected);
    if (!exists) {
      setSchemaSchemas((prev) => ([...(prev || []), { schema_code: schemaSelected, count: null }]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaSelected]);

  // ==== SCHEMA-KEYWORDS: Load schemas and keywords ====
  useEffect(() => {
    if (viewMode !== "SCHEMA") return;
    let cancelled = false;

    (async () => {
      setSchemaLoading(true);
      try {
        // 1) Schemaliste laden
        const sRes = await api.get(`${API_BASE}/database/schema_keywords/schemas`);
        const list = normalizeSchemasResponse(sRes.data);
        if (!cancelled) setSchemaSchemas(list);

        // 2) Stabiles Schema bestimmen (aktuelles oder erstes aus Liste)
        const firstCode =
          (typeof list?.[0] === "string" ? list[0] : list?.[0]?.schema_code) || "";
        const codeForFetch = schemaSelected || firstCode;

        // Falls noch kein Schema gewählt war: jetzt setzen (ohne Abbruch)
        if (!schemaSelected && firstCode && !cancelled) {
          setSchemaSelected(firstCode);
        }

        // 3) Keywords für das stabile Schema laden
        if (codeForFetch) {
          const kRes = await api.get(
            `${API_BASE}/database/schema_keywords`,
            { params: { schema_code: codeForFetch } }
          );
          const arr = normalizeKeywordsResponse(kRes.data);
          if (!cancelled) setSchemaKeywords(arr);
        } else {
          if (!cancelled) setSchemaKeywords([]);
        }
      } catch (e) {
        console.error("Fehler beim Laden der Doktypliste/Keywords:", e);
        if (!cancelled) {
          // Liste leeren, Tabelle aber sichtbar lassen
          setSchemaSchemas((prev) => Array.isArray(prev) ? prev : []);
          setSchemaKeywords((prev) => Array.isArray(prev) ? prev : []);
        }
      } finally {
        if (!cancelled) setSchemaLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewMode, schemaSelected]);

  // ==== SPECIAL RULES: Load ====
  useEffect(() => {
    if (viewMode !== "SPECIAL") return;
    let cancelled = false;
    (async () => {
      try {
        setSpecialLoading(true);
        // Optional: Schemaliste auch hier laden, falls leer
        if ((schemaSchemas || []).length === 0) {
          try {
            const sRes = await api.get(`${API_BASE}/database/schema_keywords/schemas`);
            if (!cancelled) setSchemaSchemas(Array.isArray(sRes.data) ? sRes.data : []);
          } catch (e) {}
        }
        const data = await fetchSpecialRulesWithRetry(1, 200);
        if (!cancelled) {
          const list = Array.isArray(data) ? data.map(r => ({ ...r, then_field: normalizeThenFieldValue(r.then_field) })) : [];
          setSpecialRules(list);
        }
      } catch (e) {
        console.error("Fehler beim Laden der Sonderregeln:", e);
        if (!cancelled) setSpecialRules([]);
      } finally {
        if (!cancelled) setSpecialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const fetchEntries = async () => {
    try {
      const res = await api.get(`${API_BASE}/database/strg`);
      setEntries(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der STRG-Einträge:", err);
    }
  };

  // ==== EXCEL Import/Export (per aktuelle Tabelle) ====
  const getExcelKey = () => {
    switch (viewMode) {
      case "RENAME":
        return "rename";
      case "SCHEMA":
        return "schema_keywords";
      case "SPECIAL":
        return "special_rules";
      default:
        return "strg"; // Welle 46: echter xlsx-Download/-Upload für strg_config
    }
  };

  const reloadCurrentView = async () => {
    try {
      if (viewMode === "RENAME") {
        setRenameReloadNonce((n) => n + 1);
      } else if (viewMode === "SCHEMA") {
        const kRes = await api.get(`${API_BASE}/database/schema_keywords`, { params: { schema_code: schemaSelected } });
        setSchemaKeywords(Array.isArray(kRes.data) ? kRes.data : []);
        if ((schemaSchemas || []).length === 0) {
          try {
            const sRes = await api.get(`${API_BASE}/database/schema_keywords/schemas`);
            setSchemaSchemas(Array.isArray(sRes.data) ? sRes.data : []);
          } catch {}
        }
      } else if (viewMode === "SPECIAL") {
        const fresh = await fetchSpecialRulesWithRetry(1, 200);
        setSpecialRules(fresh);
      }
    } catch (e) {
      console.error("Reload nach Excel-Operation fehlgeschlagen:", e);
    }
  };

  const handleExcelDownload = async () => {
    const key = getExcelKey();
    if (!key) return;
    setBatchRunning(true);
    setBatchStatus("");
    setBatchError("");

    const tryPost = async (url, params) => api.post(url, null, { params }).then(r => r.data);

    try {
      if (key === "strg") {
        // Welle 46: echte xlsx-Datei in den Browser laden (respektiert Filter)
        const params = {};
        if (selectedIban) params.iban = selectedIban;
        if (objektFilter) params.objekt = objektFilter;
        const r = await api.get(`${API_BASE}/database/strg/export-excel`, { params, responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `strg_config_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        setBatchStatus("STRG-Excel heruntergeladen — bearbeiten und über „Excel laden“ zurückspielen.");
      } else if (key === "rename") {
        // NEU: Multi-Sheet-Export für alle Rename-Tabellen
        await api.post(`${API_BASE}/strg/export_rename_tables_excel`);
        setBatchStatus("Excel aktualisiert (RENAME: alle Rename-Tabellen).");
      } else {
        // Standard: nur aktuelle Tabelle
        await tryPost(`${API_BASE}/database/excel/export`, { table: key });
        setBatchStatus("Excel aktualisiert (Download) – nur die aktuelle Tabelle.");
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        // Fallback auf bisherige Export-Routen
        try {
          if (key === "rename") {
            await tryPost(`${API_BASE}/database/strg/export_empfaenger_excel`);
            setBatchStatus("Excel aktualisiert (Fallback: Empfänger-Export).");
          } else if (key === "schema_keywords") {
            await tryPost(`${API_BASE}/database/strg/export_schema_keywords_excel`);
            setBatchStatus("Excel aktualisiert (Fallback: Doktyp).");
          } else if (key === "special_rules") {
            await tryPost(`${API_BASE}/database/strg/export_special_rules_excel`);
            setBatchStatus("Excel aktualisiert (Fallback: Sonderregeln).");
          }
        } catch (e2) {
          setBatchError(e2?.response?.data?.detail || e2?.message || "Fehler beim Excel-Export.");
          setBatchRunning(false);
          setTimeout(() => { setBatchStatus(""); setBatchError(""); }, 4500);
          return;
        }
      } else {
        setBatchError(e?.response?.data?.detail || e?.message || "Fehler beim Excel-Export.");
        setBatchRunning(false);
        setTimeout(() => { setBatchStatus(""); setBatchError(""); }, 4500);
        return;
      }
    }

    setBatchRunning(false);
    setTimeout(() => { setBatchStatus(""); setBatchError(""); }, 4500);
  };

  // Welle 46: bearbeitete STRG-Excel/CSV hochladen — erst Dry-Run-Report,
  // bei vielen Änderungen/Fehlern Rückfrage, dann echter Import.
  const handleStrgFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBatchRunning(true);
    setBatchStatus("");
    setBatchError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const dry = (await api.post(`${API_BASE}/database/strg/import-excel?dry_run=1`, fd)).data;
      const zusammenfassung =
        `${dry.neu} neu, ${dry.geaendert} geändert, ${dry.unveraendert} unverändert` +
        (dry.fehler?.length ? `, ${dry.fehler.length} Fehler` : "");
      if (dry.fehler?.length) {
        const beispiele = dry.fehler.slice(0, 5).map((f) => `Zeile ${f.zeile}: ${f.grund}`).join("\n");
        if (!window.confirm(`Vorschau „${file.name}“: ${zusammenfassung}\n\nFehler (Auszug):\n${beispiele}\n\nTrotzdem importieren? (fehlerhafte Zeilen werden übersprungen)`)) {
          setBatchStatus("Import abgebrochen.");
          return;
        }
      } else if (dry.neu + dry.geaendert > 50) {
        if (!window.confirm(`Vorschau „${file.name}“: ${zusammenfassung}\n\nJetzt importieren?`)) {
          setBatchStatus("Import abgebrochen.");
          return;
        }
      } else if (dry.neu + dry.geaendert === 0) {
        setBatchStatus(`„${file.name}“: keine Änderungen (${zusammenfassung}).`);
        return;
      }
      const fd2 = new FormData();
      fd2.append("file", file);
      const echt = (await api.post(`${API_BASE}/database/strg/import-excel`, fd2)).data;
      setBatchStatus(`Excel importiert: ${echt.neu} neu, ${echt.geaendert} geändert, ${echt.unveraendert} unverändert` +
        (echt.fehler?.length ? `, ${echt.fehler.length} Fehler übersprungen` : "") + ".");
      fetchEntries();
    } catch (err) {
      setBatchError(err?.response?.data?.detail || err?.message || "Fehler beim Excel-Import.");
    } finally {
      setBatchRunning(false);
      setTimeout(() => { setBatchStatus(""); setBatchError(""); }, 8000);
    }
  };

  const handleExcelLoad = async () => {
    const key = getExcelKey();
    if (!key) return;
    if (key === "strg") {
      strgFileRef.current?.click();
      return;
    }
    setBatchRunning(true);
    setBatchStatus("");
    setBatchError("");

    const tryPost = async (url, params) => api.post(url, null, { params }).then(r => r.data);

    try {
      if (key === "rename") {
        // NEU: Multi-Sheet-Import für alle Rename-Tabellen
        await api.post(`${API_BASE}/strg/import_rename_tables_excel`);
        setBatchStatus("Excel geladen (RENAME: alle Rename-Tabellen).");
      } else {
        // Standard: nur aktuelle Tabelle
        await tryPost(`${API_BASE}/database/excel/import`, { table: key });
        setBatchStatus("Excel geladen (Upload) – DB aktualisiert für aktuelle Tabelle.");
      }
      await reloadCurrentView();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Fehler beim Excel‑Import.";
      setBatchError(msg);
    } finally {
      setBatchRunning(false);
      setTimeout(() => { setBatchStatus(""); setBatchError(""); }, 4500);
    }
  };

  const handleCreate = async () => {
    // Welle 46: Pflichtfelder vorab prüfen + Fehler sichtbar machen (vorher
    // verschwand ein 400 still in der Konsole → „die ID geht nicht")
    if (!String(newEntry.iban || "").trim() || !String(newEntry["ZahlungsempfängerIBAN"] || "").trim()) {
      strgToast("Konto-IBAN und Partner-IBAN sind Pflichtfelder.", true);
      return;
    }
    try {
      const r = await api.post(`${API_BASE}/database/strg`, newEntry);
      fetchEntries();
      strgToast(r.data?.created
        ? "✅ Neue Regel angelegt."
        : "✅ Bestehende Regel für dieses Konto-/Partner-Paar aktualisiert.");
      setShowNewForm(false);
      setNewEntry({
        Beguenstigter: "",
        ZahlungsempfängerIBAN: "",
        KurzName: "",
        Kategorie: "",
        SubKat: "",
        Kst: "",
        Mwst: "",
        Umlageart: "",
        BelegID: "",
        Gegenkonto: "",
        iban: "",
      });
    } catch (err) {
      strgToast(err?.response?.data?.detail || err?.message || "Fehler beim Erstellen.", true);
    }
  };

  const handleDelete = async (id) => {
    const row = entries.find((e) => e.id === id);
    if (!window.confirm(`Regel „${row?.KurzName || row?.Beguenstigter || id}“ wirklich löschen?`)) return;
    try {
      await api.delete(`${API_BASE}/database/strg/${id}`);
      fetchEntries();
      strgToast("Regel gelöscht.");
    } catch (err) {
      strgToast(err?.response?.data?.detail || err?.message || "Fehler beim Löschen.", true);
    }
  };

  const handleUpdate = async (entryId) => {
    try {
      const updated = entries.find((e) => e.id === entryId);
      if (!updated) return;
      await api.put(`${API_BASE}/database/strg/${updated.id}`, updated);
      setEditIndex(null);
      fetchEntries();
      strgToast("✅ Gespeichert.");
    } catch (err) {
      strgToast(err?.response?.data?.detail || err?.message || "Fehler beim Aktualisieren.", true);
    }
  };

  const handleRenameDelete = async (id) => {
    try {
      const row = renameEntries.find(r => r.id === id);
      if (!row) return;
      if (row.table === "absender") {
        await api.delete(`${API_BASE}/database/absender/${row.id}`);
      } else if (row.table === "mandat") {
        await api.delete(`${API_BASE}/database/empfaenger_mandat/${row.id}`);
      } else if (row.table === "versicherung") {
        await api.delete(`${API_BASE}/database/versicherungen/${row.id}`);
      } else if (row.table === "steuernummer") {
        await api.delete(`${API_BASE}/database/steuernummer/${row.id}`);
      } else if (row.table === "vertrag") {
        await api.delete(`${API_BASE}/database/vertraege/${row.id}`);
      } else {
        await api.delete(`${API_BASE}/database/empfaenger/${row.id}`, { params: { category: row.table } });
      }
      // reload current view
      if (renameCategory === "all") {
        setRenameReloadNonce((n) => n + 1); // trigger useEffect by noop state set
      } else {
        setRenameEntries(prev => prev.filter(e => e.id !== row.id));
      }
    } catch (err) {
      console.error("Fehler beim Löschen (rename):", err);
    }
  };
  const handleRenameUpdate = async (id) => {
    try {
      const updated = renameEntries.find(r => r.id === id);
      if (!updated) return;
      const targetTable = updated.table || updated._originalTable || renameCategory;
      const original = updated._originalTable || targetTable;

      // --- IBAN: spezieller Payload & Route-Params ---
      if (targetTable === "iban") {
        const payloadIban = {
          iban: updated.iban,
          bank: updated.bank,
          objekt: updated.objekt,
          person: updated.person,
          schema_code: updated.schema_code,
          priority: parseInt(updated.priority ?? 100, 10),
        };

        if (targetTable === original) {
          await api.put(`${API_BASE}/database/empfaenger/${updated.id}`, payloadIban, { params: { category: "iban" } });
        } else {
          // MOVE: neu in IBAN anlegen, alten Datensatz aus alter Tabelle entfernen
          await api.post(`${API_BASE}/database/empfaenger`, payloadIban, { params: { category: "iban" } });
          if (original === "absender") {
            await api.delete(`${API_BASE}/database/absender/${updated.id}`);
          } else {
            await api.delete(`${API_BASE}/database/empfaenger/${updated.id}`, { params: { category: original } });
          }
        }

        setRenameEditIndex(null);
        if (renameCategory === "all") {
          setRenameReloadNonce((n) => n + 1);
        } else if (renameCategory === "absender") {
          const res = await api.get(`${API_BASE}/database/absender`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : []);
        } else {
          const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
        }
        return;
      }

      // --- Mandat: spezieller Payload & Logik ---
      if (targetTable === "mandat") {
        const payloadMandat = {
          str_nr: updated.str_nr,
          mandanten_nr: updated.mandanten_nr,
          objekt: updated.objekt,
          person: updated.person,
          schema_code: updated.schema_code,
          priority: parseInt(updated.priority ?? 100, 10),
        };

        if (targetTable === original) {
          await api.put(`${API_BASE}/database/empfaenger_mandat/${updated.id}`, payloadMandat);
        } else {
          // MOVE: neu in Mandat anlegen, alten Datensatz aus alter Tabelle entfernen
          await api.post(`${API_BASE}/database/empfaenger_mandat`, payloadMandat);
          if (original === "absender") {
            await api.delete(`${API_BASE}/database/absender/${updated.id}`);
          } else {
            await api.delete(`${API_BASE}/database/empfaenger/${updated.id}`, { params: { category: original } });
          }
        }

        setRenameEditIndex(null);
        if (renameCategory === "all") {
          setRenameReloadNonce((n) => n + 1);
        } else if (renameCategory === "absender") {
          const res = await api.get(`${API_BASE}/database/absender`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : []);
        } else if (renameCategory === "mandat") {
          const res = await api.get(`${API_BASE}/database/empfaenger_mandat`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "mandat", _originalTable: "mandat" })) : []);
        } else if (renameCategory === "versicherung") {
          const res = await api.get(`${API_BASE}/database/versicherungen`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "versicherung", _originalTable: "versicherung" })) : []);
        } else if (renameCategory === "steuernummer") {
          const res = await api.get(`${API_BASE}/database/steuernummer`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "steuernummer", _originalTable: "steuernummer" })) : []);
        } else {
          const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
        }
        return;
      }

      // --- Versicherung: spezieller Payload & Logik ---
      if (targetTable === "versicherung") {
        const payloadVers = {
          versicherungsnummer: updated.versicherungsnummer,
          objekt_kuerzel: updated.objekt_kuerzel,
          objekt_name: updated.objekt_name,
          gesellschaft: updated.gesellschaft,
          gesellschaft_kuerzel: updated.gesellschaft_kuerzel,
          priority: parseInt(updated.priority ?? 100, 10),
          enabled: updated.enabled ? 1 : 0,
        };

        if (targetTable === original) {
          await api.put(`${API_BASE}/database/versicherungen/${updated.id}`, payloadVers);
        } else {
          // MOVE: neu in Versicherung anlegen, alten Datensatz aus alter Tabelle entfernen
          await api.post(`${API_BASE}/database/versicherungen`, payloadVers);
          if (original === "absender") {
            await api.delete(`${API_BASE}/database/absender/${updated.id}`);
          } else if (original === "mandat") {
            await api.delete(`${API_BASE}/database/empfaenger_mandat/${updated.id}`);
          } else {
            await api.delete(`${API_BASE}/database/empfaenger/${updated.id}`, { params: { category: original } });
          }
        }

        setRenameEditIndex(null);
        if (renameCategory === "all") {
          setRenameReloadNonce((n) => n + 1);
        } else if (renameCategory === "versicherung") {
          const res = await api.get(`${API_BASE}/database/versicherungen`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "versicherung", _originalTable: "versicherung" })) : []);
        } else if (renameCategory === "absender") {
          const res = await api.get(`${API_BASE}/database/absender`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : []);
        } else if (renameCategory === "mandat") {
          const res = await api.get(`${API_BASE}/database/empfaenger_mandat`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "mandat", _originalTable: "mandat" })) : []);
        } else {
          const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
        }
        return;
      }

      // --- Steuernummer: spezieller Payload & Logik ---
      if (targetTable === "steuernummer") {
        const payloadStnr = {
          suchbegriff: updated.suchbegriff,
          name: updated.name,
          steuernr: updated.steuernr,
          bufa_nr: updated.bufa_nr,
          finanzamt: updated.finanzamt,
          genutzt_fuer: updated.genutzt_fuer,
          alt_steuernr: updated.alt_steuernr,
          enabled: updated.enabled ? 1 : 0,
        };

        await api.put(`${API_BASE}/database/steuernummer/${updated.id}`, payloadStnr);

        setRenameEditIndex(null);
        if (renameCategory === "all") {
          setRenameReloadNonce((n) => n + 1);
        } else if (renameCategory === "steuernummer") {
          const res = await api.get(`${API_BASE}/database/steuernummer`);
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "steuernummer", _originalTable: "steuernummer" })) : []);
        } else {
          const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
          setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
        }
        return;
      }

      // --- Vertrag: spezieller Payload & Logik ---
      if (targetTable === "vertrag") {
        const payloadVertrag = {
          objekt_id: updated.objekt_id,
          art: updated.art,
          lieferant: updated.lieferant,
          vertragsnummer: updated.vertragsnummer,
          vertrag_id: updated.vertrag_id,
          kundennummer: updated.kundennummer,
          zaehlernummer: updated.zaehlernummer,
          marktlocation: updated.marktlocation,
          vertragskonto: updated.vertragskonto,
          vertrag_beginn: updated.vertrag_beginn,
          vertrag_ende: updated.vertrag_ende,
          vertrags_partner: updated.vertrags_partner,
          gekuendigt: updated.gekuendigt,
        };
        await api.put(`${API_BASE}/database/vertraege/${updated.id}`, payloadVertrag);
        setRenameEditIndex(null);
        if (renameCategory === "all") {
          setRenameReloadNonce((n) => n + 1);
        } else if (renameCategory === "vertrag") {
          const res = await api.get(`${API_BASE}/database/vertraege`);
          setRenameEntries(Array.isArray(res.data?.items) ? res.data.items.map(x => ({ ...x, table: "vertrag", _originalTable: "vertrag" })) : []);
        }
        return;
      }

      const payload = {
        bezeichnung: updated.bezeichnung || "",
        kuerzel: updated.kuerzel || "",
        schema: updated.schema || "",
        person: updated.person || "",
        priority: parseInt(updated.priority ?? 100, 10),
      };

      if (targetTable === original) {
        // normales Update
        if (targetTable === "absender") {
          await api.put(`${API_BASE}/database/absender/${updated.id}`, payload);
        } else {
          await api.put(`${API_BASE}/database/empfaenger/${updated.id}`, payload, { params: { category: targetTable } });
        }
      } else {
        // MOVE: in neues Ziel posten, altes löschen
        if (targetTable === "absender") {
          await api.post(`${API_BASE}/database/absender`, payload);
        } else {
          await api.post(`${API_BASE}/database/empfaenger`, payload, { params: { category: targetTable } });
        }
        if (original === "absender") {
          await api.delete(`${API_BASE}/database/absender/${updated.id}`);
        } else {
          await api.delete(`${API_BASE}/database/empfaenger/${updated.id}`, { params: { category: original } });
        }
      }

      setRenameEditIndex(null);
      // Reload Liste entsprechend der aktuellen Ansicht
      if (renameCategory === "all") {
        // trigger reload
        setRenameReloadNonce((n) => n + 1);
      } else if (renameCategory === "absender") {
        const res = await api.get(`${API_BASE}/database/absender`);
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : []);
      } else {
        const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
      }
    } catch (err) {
      console.error("Fehler beim Aktualisieren (rename):", err);
    }
  };
  const handleRenameCreate = async () => {
    try {
      const target = renameNewEntry.table || (renameCategory === "all" ? "person" : renameCategory);
      if (target === "objekt" && !String(renameNewEntry.person || "").trim()) {
        setRenameFormError('Bitte "Person" angeben.');
        return;
      }
      setRenameFormError("");
      const isIban = target === "iban";
      const isMandat = target === "mandat";
      const isVersicherung = target === "versicherung";
      const isSteuernummer = target === "steuernummer";
      const isVertrag = target === "vertrag";
      const payload = isIban
        ? {
            iban: renameNewEntry.iban,
            bank: renameNewEntry.bank,
            objekt: renameNewEntry.objekt,
            person: renameNewEntry.person,
            schema_code: renameNewEntry.schema_code,
            priority: parseInt(renameNewEntry.priority ?? 100, 10),
          }
        : isMandat
        ? {
            str_nr: renameNewEntry.str_nr,
            mandanten_nr: renameNewEntry.mandanten_nr,
            objekt: renameNewEntry.objekt,
            person: renameNewEntry.person,
            schema_code: renameNewEntry.schema_code,
            priority: parseInt(renameNewEntry.priority ?? 100, 10),
          }
        : isVersicherung
        ? {
            versicherungsnummer: renameNewEntry.versicherungsnummer,
            objekt_kuerzel: renameNewEntry.objekt_kuerzel,
            objekt_name: renameNewEntry.objekt_name,
            gesellschaft: renameNewEntry.gesellschaft,
            gesellschaft_kuerzel: renameNewEntry.gesellschaft_kuerzel,
            priority: parseInt(renameNewEntry.priority ?? 100, 10),
            enabled: renameNewEntry.enabled ? 1 : 0,
          }
        : isSteuernummer
        ? {
            suchbegriff: renameNewEntry.suchbegriff,
            name: renameNewEntry.name,
            steuernr: renameNewEntry.steuernr,
            bufa_nr: renameNewEntry.bufa_nr,
            finanzamt: renameNewEntry.finanzamt,
            genutzt_fuer: renameNewEntry.genutzt_fuer,
            alt_steuernr: renameNewEntry.alt_steuernr,
            enabled: renameNewEntry.enabled ? 1 : 0,
          }
        : isVertrag
        ? {
            objekt_id: renameNewEntry.objekt_id || null,
            art: renameNewEntry.art || null,
            lieferant: renameNewEntry.lieferant || null,
            vertragsnummer: renameNewEntry.vertragsnummer || null,
            kundennummer: renameNewEntry.kundennummer || null,
            zaehlernummer: renameNewEntry.zaehlernummer || null,
            // Welle 46: Laufzeit direkt bei der Neuanlage mitgeben
            vertrag_beginn: renameNewEntry.vertrag_beginn || null,
            vertrag_ende: renameNewEntry.vertrag_ende || null,
          }
        : {
            bezeichnung: renameNewEntry.bezeichnung,
            kuerzel: renameNewEntry.kuerzel,
            priority: parseInt(renameNewEntry.priority ?? 100, 10),
            ...(target === "objekt" ? { person: renameNewEntry.person } : { schema: renameNewEntry.schema }),
          };
      if (target === "absender") {
        await api.post(`${API_BASE}/database/absender`, payload);
      } else if (target === "mandat") {
        await api.post(`${API_BASE}/database/empfaenger_mandat`, payload);
      } else if (target === "versicherung") {
        await api.post(`${API_BASE}/database/versicherungen`, payload);
      } else if (target === "steuernummer") {
        await api.post(`${API_BASE}/database/steuernummer`, payload);
      } else if (target === "vertrag") {
        await api.post(`${API_BASE}/database/vertraege`, payload);
      } else {
        await api.post(`${API_BASE}/database/empfaenger`, payload, { params: { category: target } });
      }
      {
        const next = TABLE_CODES.includes(renameCategory) ? renameCategory : "person";
        setRenameNewEntry({
          bezeichnung: "",
          kuerzel: "",
          schema: "",
          person: "",
          priority: 100,
          table: next,
          // IBAN-spezifisch
          iban: "",
          bank: "",
          objekt: "",
          schema_code: "",
          // Mandat-spezifisch
          str_nr: "",
          mandanten_nr: "",
        });
      }
      setRenameFormError("");
      setRenameNewOpen(false);
      // Reload Liste
      if (renameCategory === "all") {
        setRenameReloadNonce((n) => n + 1);
      } else if (renameCategory === "absender") {
        const res = await api.get(`${API_BASE}/database/absender`);
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "absender", _originalTable: "absender" })) : []);
      } else if (renameCategory === "mandat") {
        const res = await api.get(`${API_BASE}/database/empfaenger_mandat`);
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "mandat", _originalTable: "mandat" })) : []);
      } else if (renameCategory === "versicherung") {
        const res = await api.get(`${API_BASE}/database/versicherungen`);
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "versicherung", _originalTable: "versicherung" })) : []);
      } else if (renameCategory === "steuernummer") {
        const res = await api.get(`${API_BASE}/database/steuernummer`);
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: "steuernummer", _originalTable: "steuernummer" })) : []);
      } else if (renameCategory === "vertrag") {
        const res = await api.get(`${API_BASE}/database/vertraege`);
        setRenameEntries(Array.isArray(res.data?.items) ? res.data.items.map(x => ({ ...x, table: "vertrag", _originalTable: "vertrag" })) : []);
      } else {
        const res = await api.get(`${API_BASE}/database/empfaenger`, { params: { category: renameCategory } });
        setRenameEntries(Array.isArray(res.data) ? res.data.map(x => ({ ...x, table: renameCategory, _originalTable: renameCategory })) : []);
      }
    } catch (err) {
      console.error("Fehler beim Erstellen (rename):", err);
      setRenameFormError(err?.response?.data?.detail || err?.message || "Fehler beim Speichern");
    }
  };

  // ==== SCHEMA-KEYWORDS: Handlers ====
  const handleSchemaCreate = async () => {
    try {
      const codeNum = encodeMatchMode(schemaNewEntry.match_mode);

      // Optimistisches Update: hält die Tabelle sichtbar und verhindert ein "Flackern"
      const optimistic = {
        id: `tmp-${Date.now()}`,
        token: schemaNewEntry.token,
        // Für die Anzeige nutzen wir hier den String-Modus; Backend bekommt numeric
        match_mode: schemaNewEntry.match_mode,
        priority: parseInt(schemaNewEntry.priority ?? 100, 10),
        enabled: schemaNewEntry.enabled ? 1 : 0,
        erweiterung: schemaNewEntry.erweiterung,
        schema_code: schemaSelected,
      };
      setSchemaKeywords((prev) => (Array.isArray(prev) ? [...prev, optimistic] : [optimistic]));

      // Tatsächliches Anlegen im Backend
      const payload = {
        token: schemaNewEntry.token,
        match_mode: codeNum,     // numeric für Backend
        mode: codeNum,
        matchmode: codeNum,
        priority: optimistic.priority,
        enabled: optimistic.enabled,
        erweiterung: optimistic.erweiterung,
        schema_code: schemaSelected,
      };
      await api.post(`${API_BASE}/database/schema_keywords`, payload);

      // Formular zurücksetzen und schließen
      setSchemaNewEntry({ token: "", match_mode: "substr", priority: 100, enabled: 1, erweiterung: "" });
      setSchemaNewOpen(false);

      // Liste frisch laden (ersetzt ggf. den temp-Eintrag)
      const kRes = await api.get(`${API_BASE}/database/schema_keywords`, { params: { schema_code: schemaSelected } });
      const arr = normalizeKeywordsResponse(kRes.data);
      setSchemaKeywords(arr);
    } catch (err) {
      console.error("Fehler beim Erstellen (Schema‑Keyword):", err);
    }
  };
  const handleSchemaDelete = async (id) => {
    try {
      await api.delete(`${API_BASE}/database/schema_keywords/${id}`);
      setSchemaKeywords((prev) => (Array.isArray(prev) ? prev : []).filter((e) => e.id !== id));
    } catch (err) { console.error("Fehler beim Löschen (Schema‑Keyword):", err); }
  };
  const handleSchemaUpdate = async (index) => {
    try {
      const updated = schemaKeywords[index];
      const code = encodeMatchMode(updated.match_mode);
      const payload = {
        token: updated.token,
        match_mode: code,
        mode: code,
        matchmode: code,
        priority: parseInt(updated.priority ?? 100, 10),
        enabled: updated.enabled ? 1 : 0,
        erweiterung: updated.erweiterung,
        schema_code: schemaSelected,
      };
      await api.put(`${API_BASE}/database/schema_keywords/${updated.id}`, payload);
      setSchemaEditIndex(null);
      const kRes = await api.get(`${API_BASE}/database/schema_keywords`, { params: { schema_code: schemaSelected } });
      setSchemaKeywords(normalizeKeywordsResponse(kRes.data));
    } catch (err) { console.error("Fehler beim Aktualisieren (Schema‑Keyword):", err); }
  };

  // ==== SPECIAL RULES: Handlers ====
  const cleanseSpecialPayload = (obj) => ({
    ...obj,
    then_field: normalizeThenFieldValue(obj.then_field),
    aktiv: obj.aktiv ? 1 : 0,
    anchor_nextline: obj.anchor_nextline ? 1 : 0,
    window_chars: parseInt(obj.window_chars ?? 140, 10),
    extract_group: parseInt(obj.extract_group ?? 1, 10),
    postprocess_n: obj.postprocess_n === null || obj.postprocess_n === "" ? null : parseInt(obj.postprocess_n, 10),
    priority: parseInt(obj.priority ?? 100, 10),
  });

  const handleSpecialCreate = async () => {
    try {
      const payload = cleanseSpecialPayload(specialNew);
      await api.post(`${API_BASE}/database/special_rules`, payload);
      setSpecialNew({ ...specialDefault });
      setSpecialNewOpen(false);
      const fresh = await fetchSpecialRulesWithRetry(1, 200);
      setSpecialRules(fresh);
    } catch (err) { console.error("Fehler beim Erstellen (Sonderregel):", err); }
  };

  const handleSpecialDelete = async (id) => {
    try {
      await api.delete(`${API_BASE}/database/special_rules/${id}`);
      const fresh = await fetchSpecialRulesWithRetry(1, 200);
      setSpecialRules(fresh);
    } catch (err) { console.error("Fehler beim Löschen (Sonderregel):", err); }
  };

  const handleSpecialUpdate = async (index) => {
    try {
      const updated = cleanseSpecialPayload(specialRules[index]);
      await api.put(`${API_BASE}/database/special_rules/${updated.id}`, updated);
      setSpecialEditIndex(null);
      const fresh = await fetchSpecialRulesWithRetry(1, 200);
      setSpecialRules(fresh);
    } catch (err) { console.error("Fehler beim Aktualisieren (Sonderregel):", err); }
  };

  // Welle 46: ohne Konto-Auswahl ALLE Regeln zeigen (Objekt-Filter wirkt trotzdem)
  // statt einer leeren Tabelle; Anzeige auf 500 Zeilen gedeckelt.
  const STRG_ANZEIGE_CAP = 500;
  const filteredEntries = entries.filter(
    (entry) =>
      (!selectedIban || entry.iban === selectedIban) &&
      (!objektFilter || entry.objekt === objektFilter) &&
      (!filter ||
        entry.Beguenstigter?.toLowerCase().includes(filter.toLowerCase()) ||
        entry.KurzName?.toLowerCase().includes(filter.toLowerCase()) ||
        entry["ZahlungsempfängerIBAN"]?.toLowerCase().includes(filter.toLowerCase()))
  );
  const angezeigteEntries = filteredEntries.slice(0, STRG_ANZEIGE_CAP);

  // Vorschlagswerte für Datalists (Lücken-Formular) aus dem Regelbestand
  const strgVorschlaege = {
    kategorien: [...new Set(entries.map((e) => e.Kategorie).filter(Boolean))].sort(),
    subkats: [...new Set(entries.map((e) => e.SubKat).filter(Boolean))].sort(),
    ksts: [...new Set(entries.map((e) => e.Kst).filter(Boolean))].sort(),
    umlagearten: [...new Set(entries.map((e) => e.Umlageart).filter(Boolean))].sort(),
  };

  return (
    <div className="page-wrapper">
      <div className="segmented">
        <button
          type="button"
          className={`segmented-btn ${viewMode === "STRG" ? "active" : ""}`}
          onClick={() => setViewMode("STRG")}
        >
          CSV → Umsatz
        </button>
        <button
          type="button"
          className={`segmented-btn ${viewMode === "RENAME" ? "active" : ""}`}
          onClick={() => setViewMode("RENAME")}
        >
          rename me
        </button>
        <button
          type="button"
          className={`segmented-btn ${viewMode === "SCHEMA" ? "active" : ""}`}
          onClick={() => setViewMode("SCHEMA")}
        >
          Schema‑Keywords
        </button>
        <button
          type="button"
          className={`segmented-btn ${viewMode === "SPECIAL" ? "active" : ""}`}
          onClick={() => setViewMode("SPECIAL")}
        >
          Sonderregeln
        </button>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginBottom: 20 }}>
        {batchStatus && <span className="badge badge-success">{batchStatus}</span>}
        {batchError && <span className="badge badge-danger">{batchError}</span>}
        <button
          onClick={handleExcelLoad}
          disabled={batchRunning || !getExcelKey()}
          title="Excel → DB (nur aktuelle Tabelle)"
        >
          {batchRunning ? "…läuft" : "Excel laden"}
        </button>
        <button
          className="secondary"
          onClick={handleExcelDownload}
          disabled={batchRunning || !getExcelKey()}
          title="DB → Excel/Dropbox (nur aktuelle Tabelle)"
        >
          {batchRunning ? "…läuft" : "Excel downloaden"}
        </button>
      </div>

      {viewMode === "SCHEMA" ? (
        <>
<div className="segmented segmented-cats category-wrapper">
  <div className={`category-row ${schemaCatsExpanded ? 'expanded' : 'collapsed'}`}>
    {schemaSchemas.length === 0 ? (
      <button type="button" className="segmented-btn segmented-btn--cat" disabled>
        Keine Doktypen gefunden
      </button>
    ) : (
      (schemaCatsExpanded ? schemaSchemas : schemaSchemas.slice(0, SCHEMA_VISIBLE_COUNT)).map((s, i) => {
        const code = typeof s === "string" ? s : s?.schema_code;
        const cnt = (s && typeof s === "object" && typeof s.count === "number") ? s.count : null;
        const active = schemaSelected === code;
        return (
          <button
            key={`${code}-${i}`}
            type="button"
            className={`segmented-btn segmented-btn--cat ${active ? "active" : ""}`}
            onClick={() => setSchemaSelected(code)}
            title={cnt !== null ? `${code} (${cnt})` : code}
          >
            {code}{cnt !== null ? ` (${cnt})` : ""}
          </button>
        );
      })
    )}
  </div>
  {schemaSchemas.length > SCHEMA_VISIBLE_COUNT && (
    <button 
      type="button" 
      className={`category-toggle ${schemaCatsExpanded ? 'expanded' : ''}`}
      onClick={() => setSchemaCatsExpanded(!schemaCatsExpanded)}
    >
      {schemaCatsExpanded ? 'Weniger' : `+${schemaSchemas.length - SCHEMA_VISIBLE_COUNT} mehr`}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
  )}
</div>

{/* Freitext: neues Schema beginnen (\"unendliche Tabellen\" via frei wählbarer Schema-Code) */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "-2px 0 12px" }}>
          <input
            placeholder="Neuer Doktyp (Code)"
            value={schemaNewCode}
            onChange={(e) => setSchemaNewCode(e.target.value)}
            disabled={schemaLoading}
          />
          <button
            type="button"
            onClick={() => {
              const next = (schemaNewCode || "").trim();
              if (!next) return;
              setSchemaSelected(next);
              addSchemaIfMissing(next);
              setSchemaNewCode("");
            }}
            disabled={schemaLoading || !schemaNewCode.trim()}
            title="Doktyp auswählen oder neu beginnen"
          >
            Doktyp wählen/erstellen
          </button>
        </div>

        {/* Dateinamen-Template (pro Doktyp) */}
        <div className="template-editor-card">
          <div className="template-header">
            <strong>Dateinamen-Vorlage für Doktyp „{schemaSelected || "—"}"</strong>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {tplMsg && <span className="badge badge-success">{tplMsg}</span>}
              {tplErr && <span className="badge badge-danger">{tplErr}</span>}
              <button onClick={() => setTplOpen(o => !o)}>
                {tplOpen ? "Legende ausblenden" : "Legende anzeigen"}
              </button>
            </div>
          </div>

          {/* Template Input */}
          <div className="template-input-section">
            <div className="template-input-row">
              <input
                ref={tplInputRef}
                className="template-input"
                placeholder={toBracketTemplate(normalizeInv6(effectiveTplFor(schemaSelected)))}
                value={toBracketTemplate(normalizeInv6(uiTplValue(schemaSelected)))}
                onChange={(e) => handleTplChange(schemaSelected, e.target.value)}
                disabled={tplLoading}
              />
              <div className="btn-group">
                <button
                  className="icon-btn success"
                  title={canSaveTpl(schemaSelected) ? "Speichern" : "Bitte zuerst ein Keyword anlegen"}
                  onClick={(e) => { e.preventDefault(); handleTplSave(); }}
                  disabled={tplSaving || tplLoading}
                >
                  <FaCheck />
                </button>
                <button 
                  className="icon-btn danger" 
                  title="Auf Fallback zurücksetzen" 
                  onClick={() => handleTplReset(schemaSelected)} 
                  disabled={tplSaving || tplLoading}
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="template-preview">
              <span className="preview-label">Vorschau:</span>
              <span className="preview-value">{samplePreview(uiTplValue(schemaSelected), schemaSelected)}</span>
            </div>
          </div>

          {/* Klickbare Platzhalter-Legende */}
          {tplOpen && (
            <div className="template-legend-new">
              <div className="legend-header">
                <span className="legend-title">Platzhalter anklicken zum Einfügen:</span>
              </div>
              
              {Object.entries(PLACEHOLDER_CATEGORIES).map(([catKey, catInfo]) => {
                const items = ALL_PLACEHOLDERS.filter(p => p.category === catKey);
                if (items.length === 0) return null;
                return (
                  <div key={catKey} className="placeholder-category">
                    <div className="category-label" style={{ borderLeftColor: catInfo.color }}>
                      {catInfo.label}
                    </div>
                    <div className="placeholder-chips">
                      {items.map(p => (
                        <button
                          key={p.code}
                          type="button"
                          className="placeholder-chip"
                          style={{ '--chip-color': catInfo.color }}
                          onClick={() => insertPlaceholder(p.code)}
                          title={p.desc}
                        >
                          <span className="chip-label">{p.label}</span>
                          <span className="chip-code">{p.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              <div className="freetext-hint">
                <span>💡 Du kannst auch Freitext einfügen, z.B. Trennzeichen wie <code> - </code> oder <code>_</code></span>
              </div>
            </div>
          )}
        </div>

        {/* Neues Keyword – inline in der Tabelle */}
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            {!schemaNewOpen ? (
              <button onClick={() => setSchemaNewOpen(true)}>Neues Keyword</button>
            ) : (
              <button className="secondary" onClick={() => setSchemaNewOpen(false)}>Neues Keyword verbergen</button>
            )}
          </div>

          <div className="table-filter-wrapper"><table className="strg-table strg-table--rename">
            <thead>
              <tr>
                <th className="filterable">
                  <TableFilter 
                    column="schema_code" 
                    data={schemaKeywords} 
                    filters={keywordFilters} 
                    setFilters={setKeywordFilters}
                    activeColumn={keywordFilterCol}
                    setActiveColumn={setKeywordFilterCol}
                  />
                </th>
                <th className="filterable">
                  <TableFilter 
                    column="token" 
                    data={schemaKeywords} 
                    filters={keywordFilters} 
                    setFilters={setKeywordFilters}
                    activeColumn={keywordFilterCol}
                    setActiveColumn={setKeywordFilterCol}
                  />
                </th>
                <th className="filterable">
                  <TableFilter 
                    column="match_mode" 
                    data={schemaKeywords} 
                    filters={keywordFilters} 
                    setFilters={setKeywordFilters}
                    activeColumn={keywordFilterCol}
                    setActiveColumn={setKeywordFilterCol}
                  />
                </th>
                <th>Priorität</th>
                <th className="filterable">
                  <TableFilter 
                    column="enabled" 
                    data={(Array.isArray(schemaKeywords) ? schemaKeywords : []).map(k => ({...k, enabled: k.enabled ? 'Ja' : 'Nein'}))} 
                    filters={keywordFilters} 
                    setFilters={setKeywordFilters}
                    activeColumn={keywordFilterCol}
                    setActiveColumn={setKeywordFilterCol}
                  />
                </th>
                <th title="Ersetzt den Freitext im Dateinamen (z.B. 'Auszug' → 'Saldenmitteilung')">Erweiterung</th>
                <th className="col-actions">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {schemaNewOpen && (
                <tr className="inline-new-row">
                  <td>
                    <input
                      placeholder="Doktyp"
                      value={schemaSelected}
                      readOnly
                    />
                  </td>
                  <td>
                    <input
                      placeholder="Schlüsselwort"
                      value={schemaNewEntry.token}
                      onChange={(e) =>
                        setSchemaNewEntry({ ...schemaNewEntry, token: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={schemaNewEntry.match_mode}
                      onChange={(e) =>
                        setSchemaNewEntry({ ...schemaNewEntry, match_mode: e.target.value })
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="substr">substr</option>
                      <option value="word">word</option>
                      <option value="nummer">nummer</option>
                      <option value="zeit">zeit</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      placeholder="Priorität"
                      value={schemaNewEntry.priority}
                      onChange={(e) =>
                        setSchemaNewEntry({
                          ...schemaNewEntry,
                          priority: parseInt(e.target.value || "0", 10),
                        })
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={schemaNewEntry.enabled ? true : false}
                      onChange={(e) =>
                        setSchemaNewEntry({
                          ...schemaNewEntry,
                          enabled: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      placeholder="z.B. Saldenmitteilung"
                      title="Ersetzt den Freitext im Dateinamen wenn dieses Keyword gefunden wird"
                      value={schemaNewEntry.erweiterung}
                      onChange={(e) =>
                        setSchemaNewEntry({ ...schemaNewEntry, erweiterung: e.target.value })
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td>
                    <button onClick={handleSchemaCreate} style={{ marginRight: 8 }}>
                      Speichern
                    </button>
                    <button
                      onClick={() => {
                        setSchemaNewOpen(false);
                        setSchemaNewEntry({
                          token: "",
                          match_mode: "substr",
                          priority: 100,
                          enabled: 1,
                          erweiterung: "",
                        });
                      }}
                    >
                      Abbrechen
                    </button>
                  </td>
                </tr>
              )}
              {applyTableFilters(Array.isArray(schemaKeywords) ? schemaKeywords : [], keywordFilters).map((row, index) => (
                <tr key={`${row?.id ?? "row"}-${index}`}>
                  <td>{row.schema_code || schemaSelected}</td>
                  <td>{schemaEditIndex === index ? <input value={row.token || ""} onChange={(e) => setSchemaKeywords(prev => (Array.isArray(prev) ? prev : []).map((it, i) => i === index ? { ...it, token: e.target.value } : it))} /> : (row.token || "–")}</td>
                  <td>
                    {schemaEditIndex === index ? (
                      <select value={normalizeMatchMode(row.match_mode || "substr")} onChange={(e) => setSchemaKeywords(prev => (Array.isArray(prev) ? prev : []).map((it, i) => i === index ? { ...it, match_mode: e.target.value } : it))}>
                        <option value="substr">substr</option>
                        <option value="word">word</option>
                        <option value="nummer">nummer</option>
                        <option value="zeit">zeit</option>
                      </select>
                    ) : (
                      normalizeMatchMode(row.match_mode)
                    )}
                  </td>
                  <td>{schemaEditIndex === index ? <input type="number" value={row.priority ?? 100} onChange={(e) => setSchemaKeywords(prev => (Array.isArray(prev) ? prev : []).map((it, i) => i === index ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))} /> : (row.priority ?? 100)}</td>
                  <td>{schemaEditIndex === index ? (
                    <input type="checkbox" checked={(row.enabled ?? 1) ? true : false} onChange={(e) => setSchemaKeywords(prev => (Array.isArray(prev) ? prev : []).map((it, i) => i === index ? { ...it, enabled: e.target.checked ? 1 : 0 } : it))} />
                  ) : ((row.enabled ?? 1) ? "Ja" : "Nein")}</td>
                  <td>{schemaEditIndex === index ? <input style={{ width: "100%" }} placeholder="z.B. Saldenmitteilung" value={row.erweiterung || ""} onChange={(e) => setSchemaKeywords(prev => (Array.isArray(prev) ? prev : []).map((it, i) => i === index ? { ...it, erweiterung: e.target.value } : it))} /> : (row.erweiterung || "")}</td>
                  <td className="col-actions">
                    {schemaEditIndex === index ? (
                      <div className="btn-group">
                        <button
                          className="icon-btn success"
                          title="Speichern"
                          onClick={() => handleSchemaUpdate(index)}
                        >
                          <FaCheck />
                        </button>
                        <button
                          className="icon-btn danger"
                          title="Abbrechen"
                          onClick={() => setSchemaEditIndex(null)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <div className="btn-group">
                        <button
                          className="icon-btn"
                          title="Bearbeiten"
                          onClick={() => setSchemaEditIndex(index)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="icon-btn danger"
                          title="Löschen"
                          onClick={() => handleSchemaDelete(row.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {(Array.isArray(schemaKeywords) ? schemaKeywords.length === 0 : true) && (
                <tr><td colSpan={7} style={{ textAlign: "center", color: "#777" }}>Keine Keywords gefunden.</td></tr>
              )}
            </tbody>
          </table></div>

          {/* Neues Keyword-Formular wurde nach oben verlegt */}
        </>
      ) : viewMode === "RENAME" ? (
        <>
      <div className="segmented segmented-cats">
        {(renameCategories.length === 0 ? DEFAULT_EMPFAENGER_CATEGORIES : renameCategories).map((cat) => {
          const active = renameCategory === cat.code;
          return (
            <button
              key={cat.code}
              type="button"
              className={`segmented-btn segmented-btn--cat ${active ? "active" : ""}`}
              onClick={() => { setRenameCategory(cat.code); setRenameFilters({}); setRenameFilterCol(null); }}
              title={typeof cat.count === "number" && cat.count !== null ? `${cat.label} (${cat.count})` : cat.label}
            >
              {cat.label}{typeof cat.count === "number" && cat.count !== null ? ` (${cat.count})` : ""}
            </button>
          );
        })}
      </div>

      <div className="rename-inline-form">
        {renameFormError && renameNewOpen ? (
          <div className="badge badge-danger" style={{ marginBottom: 12 }}>
            {renameFormError}
          </div>
        ) : null}
        {!renameNewOpen ? (
          <button
          className="btn-new-entry"
          onClick={() => {
            const next = TABLE_CODES.includes(renameCategory) ? renameCategory : "person";
            setRenameNewEntry(prev => ({ ...prev, table: next }));
            setRenameFormError("");
            setRenameNewOpen(true);
          }}
        >
          Neuen Eintrag erstellen
        </button>
        ) : (
          <table className="strg-table inline-form-table no-header">
            <tbody>
              <tr>
                <td>
                  {renameNewEntry.table === "iban" ? (
                    <input
                      placeholder="Doktyp (Schema-Code)"
                      value={renameNewEntry.schema_code}
                      onChange={(e) => setRenameNewEntry({ ...renameNewEntry, schema_code: e.target.value })}
                    />
                  ) : renameNewEntry.table === "mandat" ? (
                    <input
                      placeholder="Doktyp (Schema-Code)"
                      value={renameNewEntry.schema_code}
                      onChange={(e) => setRenameNewEntry({ ...renameNewEntry, schema_code: e.target.value })}
                    />
                  ) : renameNewEntry.table === "versicherung" ? (
                    <span style={{ color: "#888", fontSize: 12 }}>Versicherung</span>
                  ) : renameNewEntry.table === "steuernummer" ? (
                    <span style={{ color: "#888", fontSize: 12 }}>Steuernummer</span>
                  ) : renameNewEntry.table === "vertrag" ? (
                    <span style={{ color: "#888", fontSize: 12 }}>Vertrag</span>
                  ) : renameNewEntry.table === "objekt" ? (
                    <input
                      placeholder="Person"
                      value={renameNewEntry.person}
                      onChange={(e) => setRenameNewEntry({ ...renameNewEntry, person: e.target.value })}
                    />
                  ) : (
                    <input
                      placeholder="Doktyp"
                      value={renameNewEntry.schema}
                      onChange={(e) => setRenameNewEntry({ ...renameNewEntry, schema: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  <select
                    value={renameNewEntry.table}
                    onChange={(e) => setRenameNewEntry({ ...renameNewEntry, table: e.target.value })}
                    style={{ width: "100%" }}
                  >
                    {TABLE_OPTIONS.map(opt => (
                      <option key={opt.code} value={opt.code}>{opt.label}</option>
                    ))}
                  </select>
                </td>
                {renameNewEntry.table === "iban" ? (
                  <>
                    <td>
                      <input
                        placeholder="IBAN"
                        value={renameNewEntry.iban}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, iban: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Bank"
                        value={renameNewEntry.bank}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, bank: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Objekt"
                        value={renameNewEntry.objekt}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, objekt: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Person"
                        value={renameNewEntry.person}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, person: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Priorität"
                        value={renameNewEntry.priority}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, priority: parseInt(e.target.value || "0", 10) })}
                      />
                    </td>
                  </>
                ) : renameNewEntry.table === "mandat" ? (
                  <>
                    <td>
                      <input
                        placeholder="str_nr"
                        value={renameNewEntry.str_nr}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, str_nr: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="mandanten_nr"
                        value={renameNewEntry.mandanten_nr}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, mandanten_nr: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="objekt"
                        value={renameNewEntry.objekt}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, objekt: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="person"
                        value={renameNewEntry.person}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, person: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Priorität"
                        value={renameNewEntry.priority}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, priority: parseInt(e.target.value || "0", 10) })}
                      />
                    </td>
                  </>
                ) : renameNewEntry.table === "versicherung" ? (
                  <>
                    <td>
                      <input
                        placeholder="Versicherungsnummer"
                        value={renameNewEntry.versicherungsnummer || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, versicherungsnummer: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Gesellschaft"
                        value={renameNewEntry.gesellschaft || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, gesellschaft: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Gesellschaft Kürzel"
                        value={renameNewEntry.gesellschaft_kuerzel || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, gesellschaft_kuerzel: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Objekt Kürzel"
                        value={renameNewEntry.objekt_kuerzel || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, objekt_kuerzel: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Objekt Name"
                        value={renameNewEntry.objekt_name || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, objekt_name: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Priorität"
                        value={renameNewEntry.priority}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, priority: parseInt(e.target.value || "0", 10) })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!renameNewEntry.enabled}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, enabled: e.target.checked ? 1 : 0 })}
                      /> Aktiviert
                    </td>
                  </>
                ) : renameNewEntry.table === "steuernummer" ? (
                  <>
                    <td>
                      <input
                        placeholder="Suchbegriff"
                        value={renameNewEntry.suchbegriff || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, suchbegriff: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Name"
                        value={renameNewEntry.name || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, name: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Steuernummer"
                        value={renameNewEntry.steuernr || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, steuernr: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Finanzamt"
                        value={renameNewEntry.finanzamt || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, finanzamt: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Genutzt für"
                        value={renameNewEntry.genutzt_fuer || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, genutzt_fuer: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Alt. Steuernr"
                        value={renameNewEntry.alt_steuernr || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, alt_steuernr: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!renameNewEntry.enabled}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, enabled: e.target.checked ? 1 : 0 })}
                      /> Aktiviert
                    </td>
                  </>
                ) : renameNewEntry.table === "vertrag" ? (
                  <>
                    <td>
                      <input
                        placeholder="Objekt-ID"
                        value={renameNewEntry.objekt_id || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, objekt_id: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Art (z.B. Strom)"
                        value={renameNewEntry.art || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, art: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Lieferant"
                        value={renameNewEntry.lieferant || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, lieferant: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Vertragsnr."
                        value={renameNewEntry.vertragsnummer || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, vertragsnummer: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Kundennr."
                        value={renameNewEntry.kundennummer || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, kundennummer: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Zählernr."
                        value={renameNewEntry.zaehlernummer || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, zaehlernummer: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        title="Vertragsbeginn"
                        value={renameNewEntry.vertrag_beginn || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, vertrag_beginn: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        title="Vertragsende"
                        value={renameNewEntry.vertrag_ende || ""}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, vertrag_ende: e.target.value })}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      <input
                        placeholder="Bezeichnung"
                        value={renameNewEntry.bezeichnung}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, bezeichnung: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Kürzel"
                        value={renameNewEntry.kuerzel}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, kuerzel: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Priorität"
                        value={renameNewEntry.priority}
                        onChange={(e) => setRenameNewEntry({ ...renameNewEntry, priority: parseInt(e.target.value || "0", 10) })}
                      />
                    </td>
                  </>
                )}
                <td>
                  <button onClick={handleRenameCreate} style={{ marginRight: 8 }}>Speichern</button>
                  <button
                    onClick={() => {
                      const next = TABLE_CODES.includes(renameCategory) ? renameCategory : "person";
                      setRenameNewOpen(false);
                      setRenameFormError("");
                      setRenameNewEntry({
                        bezeichnung: "",
                        kuerzel: "",
                        schema: "",
                        person: "",
                        priority: 100,
                        table: next,
                        // IBAN-spezifisch
                        iban: "",
                        bank: "",
                        objekt: "",
                        schema_code: "",
                        // Mandat-spezifisch
                        str_nr: "",
                        mandanten_nr: "",
                      });
                    }}
                  >
                    Abbrechen
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
          <div className={renameCategory === "vertrag" ? "strg-table-scroll" : undefined}>
          <table className={`strg-table${renameCategory === "vertrag" ? " strg-table--vertrag" : ""}`}>
            <thead>
            {renameCategory === "iban" ? (
            <tr>
              <th className="filterable">
                <TableFilter column="schema_code" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="table" data={renameEntries.map(r => ({...r, table: r.table || '–'}))} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="iban" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="bank" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="objekt" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="person" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th>Schema-Code</th>
              <th>Priorität</th>
              <th className="col-actions">Aktion</th>
            </tr>
          ) : renameCategory === "mandat" ? (
            <tr>
              <th className="filterable">
                <TableFilter column="schema_code" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="table" data={renameEntries.map(r => ({...r, table: r.table || '–'}))} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="str_nr" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="mandanten_nr" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="objekt" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="person" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th>schema_code</th>
              <th>priority</th>
              <th className="col-actions">Aktion</th>
            </tr>
          ) : renameCategory === "versicherung" ? (
            <tr>
              <th className="filterable">
                <TableFilter column="versicherungsnummer" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="gesellschaft" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="gesellschaft_kuerzel" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="objekt_kuerzel" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="objekt_name" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th>priority</th>
              <th>enabled</th>
              <th className="col-actions">Aktion</th>
            </tr>
          ) : renameCategory === "steuernummer" ? (
            <tr>
              <th className="filterable">
                <TableFilter column="suchbegriff" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="name" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="steuernr" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="finanzamt" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="genutzt_fuer" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable">
                <TableFilter column="alt_steuernr" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th>enabled</th>
              <th className="col-actions">Aktion</th>
            </tr>
          ) : renameCategory === "vertrag" ? (
            <tr>
              <th className="filterable" style={{ width: 60 }}>
                <TableFilter column="objekt_id" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 70 }}>
                <TableFilter column="art" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 110 }}>
                <TableFilter column="lieferant" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 90 }}>
                <TableFilter column="kuerzel" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 120 }}>
                <TableFilter column="vertragsnummer" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 100 }}>
                <TableFilter column="kundennummer" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 110 }}>
                <TableFilter column="zaehlernummer" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 110 }}>
                <TableFilter column="marktlocation" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="filterable" style={{ width: 110 }}>
                <TableFilter column="vertrags_partner" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th style={{ width: 75 }}>Beginn</th>
              <th style={{ width: 75 }}>Ende</th>
              <th className="filterable" style={{ width: 90 }}>
                <TableFilter column="suchbegriff" data={renameEntries} filters={renameFilters} setFilters={setRenameFilters} activeColumn={renameFilterCol} setActiveColumn={setRenameFilterCol} />
              </th>
              <th className="col-actions">Aktion</th>
            </tr>
          ) : (
            <tr>
              <th className="filterable">
                <TableFilter 
                  column={renameCategory === "objekt" ? "person" : "schema"}
                  data={renameEntries.map(r => ({...r, person: r.person || '–', schema: r.schema || '–'}))}
                  filters={renameFilters}
                  setFilters={setRenameFilters}
                  activeColumn={renameFilterCol}
                  setActiveColumn={setRenameFilterCol}
                />
              </th>
              <th className="filterable">
                <TableFilter 
                  column="table"
                  data={renameEntries.map(r => ({...r, table: r.table || r._originalTable || '–'}))}
                  filters={renameFilters}
                  setFilters={setRenameFilters}
                  activeColumn={renameFilterCol}
                  setActiveColumn={setRenameFilterCol}
                />
              </th>
              <th className="filterable">
                <TableFilter 
                  column="bezeichnung"
                  data={renameEntries}
                  filters={renameFilters}
                  setFilters={setRenameFilters}
                  activeColumn={renameFilterCol}
                  setActiveColumn={setRenameFilterCol}
                />
              </th>
              <th className="filterable">
                <TableFilter 
                  column="kuerzel"
                  data={renameEntries}
                  filters={renameFilters}
                  setFilters={setRenameFilters}
                  activeColumn={renameFilterCol}
                  setActiveColumn={setRenameFilterCol}
                />
              </th>
              <th>Priorität</th>
              <th className="col-actions">Aktion</th>
            </tr>
          )}
            </thead>
            <tbody>
              {applyTableFilters(Array.isArray(renameEntries) ? renameEntries : [], renameFilters).map((row, index) => {
                const target = row.table || row._originalTable || (TABLE_CODES.includes(renameCategory) ? renameCategory : "person");
                const isEditing = renameEditIndex === row.id;

                return (
                  <tr key={`${row?.id ?? "row"}-${index}`}>
                    {(target === "versicherung" || target === "vertrag" || target === "steuernummer") ? null : (
                    <td>
                    {isEditing ? (
                      target === "iban" ? (
                        <input
                          style={{ width: "100%" }}
                          value={row.schema_code || ""}
                          onChange={(e) =>
                            setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, schema_code: e.target.value } : it))
                          }
                        />
                      ) : target === "mandat" ? (
                        <input
                          style={{ width: "100%" }}
                          value={row.schema_code || ""}
                          onChange={(e) =>
                            setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, schema_code: e.target.value } : it))
                          }
                        />
                      ) : isObjRow(row) ? (
                        <input
                          style={{ width: "100%" }}
                          value={row.person || ""}
                          onChange={(e) =>
                            setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, person: e.target.value } : it))
                          }
                        />
                      ) : (
                        <input
                          style={{ width: "100%" }}
                          value={row.schema || ""}
                          onChange={(e) =>
                            setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, schema: e.target.value } : it))
                          }
                        />
                      )
                    ) : (
                      getDoktypValue(row)
                    )}
                    </td>
                    )}
                    {(target === "versicherung" || target === "vertrag" || target === "steuernummer") ? null : (
                    <td>
                      {isEditing ? (
                        <select
                          style={{ width: "100%" }}
                          value={row.table || row._originalTable || "person"}
                          onChange={(e) =>
                            setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, table: e.target.value } : it))
                          }
                        >
                          {TABLE_OPTIONS.map(opt => (
                            <option key={opt.code} value={opt.code}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        tableLabel(row.table || row._originalTable)
                      )}
                    </td>
                    )}

                    {target === "iban" ? (
                      <>
                        <td className="iban-cell">
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.iban || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, iban: e.target.value } : it))}
                            />
                          ) : (row.iban || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.bank || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, bank: e.target.value } : it))}
                            />
                          ) : (row.bank || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.objekt || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, objekt: e.target.value } : it))}
                            />
                          ) : (row.objekt || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.person || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, person: e.target.value } : it))}
                            />
                          ) : (row.person || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.schema_code || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, schema_code: e.target.value } : it))}
                            />
                          ) : (row.schema_code || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ width: "100%" }}
                              value={typeof row.priority === "number" ? row.priority : (row.priority ? Number(row.priority) : 100)}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))}
                            />
                          ) : (row.priority ?? 100)}
                        </td>
                      </>
                    ) : target === "versicherung" ? (
                      <>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.versicherungsnummer || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, versicherungsnummer: e.target.value } : it))}
                            />
                          ) : (row.versicherungsnummer || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.gesellschaft || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, gesellschaft: e.target.value } : it))}
                            />
                          ) : (row.gesellschaft || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.gesellschaft_kuerzel || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, gesellschaft_kuerzel: e.target.value } : it))}
                            />
                          ) : (row.gesellschaft_kuerzel || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.objekt_kuerzel || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, objekt_kuerzel: e.target.value } : it))}
                            />
                          ) : (row.objekt_kuerzel || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.objekt_name || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, objekt_name: e.target.value } : it))}
                            />
                          ) : (row.objekt_name || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ width: "100%" }}
                              value={typeof row.priority === "number" ? row.priority : (row.priority ? Number(row.priority) : 100)}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))}
                            />
                          ) : (row.priority ?? 100)}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={!!row.enabled}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, enabled: e.target.checked ? 1 : 0 } : it))}
                            />
                          ) : (row.enabled ? "✓" : "–")}
                        </td>
                      </>
                    ) : target === "steuernummer" ? (
                      <>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.suchbegriff || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, suchbegriff: e.target.value } : it))}
                            />
                          ) : (row.suchbegriff || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.name || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, name: e.target.value } : it))}
                            />
                          ) : (row.name || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.steuernr || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, steuernr: e.target.value } : it))}
                            />
                          ) : (row.steuernr || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.finanzamt || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, finanzamt: e.target.value } : it))}
                            />
                          ) : (row.finanzamt || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.genutzt_fuer || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, genutzt_fuer: e.target.value } : it))}
                            />
                          ) : (row.genutzt_fuer || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.alt_steuernr || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, alt_steuernr: e.target.value } : it))}
                            />
                          ) : (row.alt_steuernr || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={!!row.enabled}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, enabled: e.target.checked ? 1 : 0 } : it))}
                            />
                          ) : (row.enabled ? "✓" : "–")}
                        </td>
                      </>
                    ) : target === "mandat" ? (
                      <>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.str_nr || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, str_nr: e.target.value } : it))}
                            />
                          ) : (row.str_nr || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.mandanten_nr || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, mandanten_nr: e.target.value } : it))}
                            />
                          ) : (row.mandanten_nr || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.objekt || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, objekt: e.target.value } : it))}
                            />
                          ) : (row.objekt || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.person || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, person: e.target.value } : it))}
                            />
                          ) : (row.person || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.schema_code || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, schema_code: e.target.value } : it))}
                            />
                          ) : (row.schema_code || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ width: "100%" }}
                              value={typeof row.priority === "number" ? row.priority : (row.priority ? Number(row.priority) : 100)}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))}
                            />
                          ) : (row.priority ?? 100)}
                        </td>
                      </>
                    ) : target === "vertrag" ? (
                      <>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.objekt_id || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, objekt_id: e.target.value } : it))} />
                          ) : (row.objekt_id || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.art || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, art: e.target.value } : it))} />
                          ) : (row.art || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.lieferant || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, lieferant: e.target.value } : it))} />
                          ) : (row.lieferant || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.kuerzel || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, kuerzel: e.target.value } : it))} />
                          ) : (row.kuerzel || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.vertragsnummer || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, vertragsnummer: e.target.value } : it))} />
                          ) : (row.vertragsnummer || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.kundennummer || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, kundennummer: e.target.value } : it))} />
                          ) : (row.kundennummer || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.zaehlernummer || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, zaehlernummer: e.target.value } : it))} />
                          ) : (row.zaehlernummer || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.marktlocation || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, marktlocation: e.target.value } : it))} />
                          ) : (row.marktlocation || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input style={{ width: "100%" }} value={row.vertrags_partner || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, vertrags_partner: e.target.value } : it))} />
                          ) : (row.vertrags_partner || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input type="date" style={{ width: "100%" }} value={row.vertrag_beginn || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, vertrag_beginn: e.target.value } : it))} />
                          ) : (row.vertrag_beginn || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input type="date" style={{ width: "100%" }} value={row.vertrag_ende || ""} onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, vertrag_ende: e.target.value } : it))} />
                          ) : (row.vertrag_ende || "–")}
                        </td>
                        <td style={{ color: "#888", fontSize: 12 }}>
                          {row.suchbegriff || "–"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.bezeichnung || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, bezeichnung: e.target.value } : it))}
                            />
                          ) : (row.bezeichnung || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              style={{ width: "100%" }}
                              value={row.kuerzel || ""}
                              onChange={(e) => setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, kuerzel: e.target.value } : it))}
                            />
                          ) : (row.kuerzel || "–")}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ width: "100%" }}
                              value={typeof row.priority === "number" ? row.priority : (row.priority ? Number(row.priority) : 100)}
                              onChange={(e) =>
                                setRenameEntries(prev => prev.map(it => it.id === row.id ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))
                              }
                            />
                          ) : (
                            row.priority ?? 100
                          )}
                        </td>
                      </>
                    )}

                    <td className="col-actions">
                      {isEditing ? (
                        <div className="btn-group">
                          <button
                            className="icon-btn success"
                            title="Speichern"
                            onClick={() => handleRenameUpdate(row.id)}
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="icon-btn danger"
                            title="Abbrechen"
                            onClick={() => setRenameEditIndex(null)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group">
                          <button
                            className="icon-btn"
                            title="Bearbeiten"
                            onClick={() => setRenameEditIndex(row.id)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="icon-btn danger"
                            title="Löschen"
                            onClick={() => handleRenameDelete(row.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(Array.isArray(renameEntries) ? renameEntries.length === 0 : true) && (
                <tr>
                  <td colSpan={renameCategory === "iban" ? 9 : renameCategory === "vertrag" ? 13 : 6} style={{ textAlign: "center", color: "#777" }}>Keine Einträge gefunden.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      ) : viewMode === "SPECIAL" ? (
    <>
      <h2>Sonderregeln</h2>

      <table className="strg-table">
        <thead>
          <tr>
            <th>Aktiv</th>
            <th>Geltungsbereich</th>
            <th>Wert (z. B. Kürzel)</th>
            <th>Ziel‑Feld</th>
            <th>Operation</th>
            <th>Suchmodus</th>
            <th>Such‑Token</th>
            <th>Position</th>
            <th>Schlüssel/Mask</th>
            <th>Priorität</th>
            <th>Notizen</th>
            <th className="col-actions">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {(Array.isArray(specialRules) ? specialRules : []).map((row, index) => (
            <tr key={`${row?.id ?? "row"}-${index}`}>
              <td>
                {specialEditIndex === index ? (
                  <input
                    type="checkbox"
                    checked={(row.aktiv ?? 1) ? true : false}
                    onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, aktiv: e.target.checked ? 1 : 0 } : it))}
                  />
                ) : ((row.aktiv ?? 1) ? "Ja" : "Nein")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <select value={row.when_scope || "global"} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, when_scope: e.target.value } : it))}>
                    {OPT_SCOPE_SIMPLE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (DE_SCOPE_SIMPLE[row.when_scope] || row.when_scope || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <input style={{ width: "100%" }} value={row.kuerzel || ""} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, kuerzel: e.target.value } : it))} />
                ) : (row.kuerzel || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <select value={row.then_field || "rechnungsnummer"} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, then_field: e.target.value } : it))}>
                    {OPT_THEN_FIELD.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (DE_THEN_FIELD[row.then_field] || row.then_field || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <select value={row.write_mode || "replace"} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, write_mode: e.target.value } : it))}>
                    {OPT_WRITE_MODE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (DE_WRITE_MODE[row.write_mode] || row.write_mode || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <select value={row.search_mode || "token"} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, search_mode: e.target.value } : it))}>
                    {OPT_SEARCH_MODE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (DE_SEARCH_MODE[row.search_mode] || row.search_mode || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <input style={{ width: "100%" }} value={row.search_token || ""} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, search_token: e.target.value } : it))} />
                ) : (row.search_token || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <select value={row.pos || "gleich"} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, pos: e.target.value } : it))}>
                    {OPT_POS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (DE_POS[row.pos] || row.pos || "–")}
              </td>
              <td>
                {specialEditIndex === index ? (
                  row.then_field === "doktyp" ? (
                    <select style={{ width: "100%" }} value={row.key_mask || ""} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, key_mask: e.target.value } : it))}>
                      <option value="">-- Schema wählen --</option>
                      {SCHEMA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  ) : (
                    <input style={{ width: "100%" }} value={row.key_mask || ""} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, key_mask: e.target.value } : it))} />
                  )
                ) : (row.then_field === "doktyp" ? (SCHEMA_OPTIONS.find(o => o.value === row.key_mask)?.label || row.key_mask || "–") : (row.key_mask || "–"))}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <input type="number" style={{ width: "100%" }} value={row.priority ?? 100} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, priority: parseInt(e.target.value || "0", 10) } : it))} />
                ) : (row.priority ?? 100)}
              </td>
              <td>
                {specialEditIndex === index ? (
                  <input style={{ width: "100%" }} value={row.notes || ""} onChange={(e) => setSpecialRules(prev => prev.map((it, i) => i === index ? { ...it, notes: e.target.value } : it))} />
                ) : (row.notes || "")}
              </td>
              <td className="col-actions">
                {specialEditIndex === index ? (
                  <div className="btn-group">
                    <button
                      className="icon-btn success"
                      title="Speichern"
                      onClick={() => handleSpecialUpdate(index)}
                    >
                      <FaCheck />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Abbrechen"
                      onClick={() => setSpecialEditIndex(null)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="btn-group">
                    <button
                      className="icon-btn"
                      title="Bearbeiten"
                      onClick={() => setSpecialEditIndex(index)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Löschen"
                      onClick={() => handleSpecialDelete(row.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {(Array.isArray(specialRules) ? specialRules.length === 0 : true) && (
            <tr><td colSpan={12}>Keine Sonderregeln gefunden.</td></tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 20 }}>
        {!specialNewOpen ? (
          <button onClick={() => setSpecialNewOpen(true)}>Neue Sonderregel</button>
        ) : (
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Neue Sonderregel erstellen</h3>
            <div className="grid-form" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={specialNew.aktiv ? true : false} onChange={(e) => setSpecialNew({ ...specialNew, aktiv: e.target.checked ? 1 : 0 })} /> Aktiv
              </label>
              <select value={specialNew.when_scope} onChange={(e) => setSpecialNew({ ...specialNew, when_scope: e.target.value })}>
                {OPT_SCOPE_SIMPLE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <input placeholder="Wert/Kürzel" value={specialNew.kuerzel} onChange={(e) => setSpecialNew({ ...specialNew, kuerzel: e.target.value })} />
              <select value={specialNew.then_field} onChange={(e) => setSpecialNew({ ...specialNew, then_field: e.target.value })}>
                {OPT_THEN_FIELD.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <select value={specialNew.write_mode} onChange={(e) => setSpecialNew({ ...specialNew, write_mode: e.target.value })}>
                {OPT_WRITE_MODE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <select value={specialNew.search_mode} onChange={(e) => setSpecialNew({ ...specialNew, search_mode: e.target.value })}>
                {OPT_SEARCH_MODE.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <input placeholder="Such‑Token" value={specialNew.search_token} onChange={(e) => setSpecialNew({ ...specialNew, search_token: e.target.value })} />
              <select value={specialNew.pos} onChange={(e) => setSpecialNew({ ...specialNew, pos: e.target.value })}>
                {OPT_POS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              {specialNew.then_field === "doktyp" ? (
                <select value={specialNew.key_mask} onChange={(e) => setSpecialNew({ ...specialNew, key_mask: e.target.value })}>
                  <option value="">-- Schema wählen --</option>
                  {SCHEMA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              ) : (
                <input placeholder="Schlüssel/Mask" value={specialNew.key_mask} onChange={(e) => setSpecialNew({ ...specialNew, key_mask: e.target.value })} />
              )}
              <input type="number" placeholder="Priorität" value={specialNew.priority} onChange={(e) => setSpecialNew({ ...specialNew, priority: parseInt(e.target.value || '0', 10) })} />
            </div>
            <div style={{ marginTop: 16 }}>
              <button onClick={handleSpecialCreate}>Speichern</button>
              <button className="secondary" onClick={() => { setSpecialNewOpen(false); setSpecialNew({ ...specialDefault }); }}>Abbrechen</button>
            </div>
          </div>
        )}
      </div>
    </>
    ) : (
      <>
        <div className="flex items-center justify-between mb-2">
          <h2 style={{ margin: 0 }}>STRG-Tabelle</h2>
          <div className="sync-buttons-group">
            {/* Welle 39: Excel-STRG → strg_config Migration */}
            <MigrateExcelButton onDone={() => window.location.reload()} />
            {/* STRG → Daten Synchronisation */}
            <SyncDatenButton selectedIban={selectedIban} />
            {/* Verträge → Daten Synchronisation */}
            <SyncVertraegeButton selectedIban={selectedIban} />
          </div>
        </div>

        {/* Welle 46: versteckter Datei-Input für „Excel laden" (STRG-Upload) */}
        <input type="file" ref={strgFileRef} accept=".xlsx,.csv" style={{ display: "none" }}
               onChange={handleStrgFileSelected} />

        {/* Welle 46: Google-Sheet-Quellen (Auto-Sync beim Öffnen) */}
        <SheetSyncBereich onSynced={fetchEntries} />

        {/* Welle 46: Umschalter Regeln | Lücken + Zähler + Meldungen */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
          <div className="segmented">
            <button type="button" className={`segmented-btn ${strgView === "regeln" ? "active" : ""}`}
                    onClick={() => setStrgView("regeln")}>
              Regeln ({entries.length})
            </button>
            <button type="button" className={`segmented-btn ${strgView === "luecken" ? "active" : ""}`}
                    onClick={() => setStrgView("luecken")}>
              🔍 Lücken
            </button>
          </div>
          {strgView === "regeln" && (
            <span style={{ fontSize: 12, opacity: 0.75 }}>
              {filteredEntries.length} im Filter
              {filteredEntries.length > STRG_ANZEIGE_CAP ? ` · ${STRG_ANZEIGE_CAP} angezeigt — bitte weiter filtern` : ""}
            </span>
          )}
          {strgMsg && <span className="badge badge-success">{strgMsg}</span>}
          {strgErr && <span className="badge badge-danger">{strgErr}</span>}
        </div>

        <div className="filter-bar">
          {/* Welle 39: Objekt-Filter — schränkt die Konto-Auswahl ein */}
          <select value={objektFilter} onChange={(e) => { setObjektFilter(e.target.value); setSelectedIban(""); }}>
            <option value="">-- alle Objekte --</option>
            {[...new Set(entries.map((e) => e.objekt).filter(Boolean))].sort().map((obj) => (
              <option key={obj} value={obj}>{obj}</option>
            ))}
          </select>
          <select value={selectedIban} onChange={(e) => setSelectedIban(e.target.value)}>
            <option value="">-- Konto auswählen --</option>
            {(() => {
              const seen = new Map();
              entries.forEach((e) => {
                if (!e.iban || seen.has(e.iban)) return;
                if (objektFilter && e.objekt !== objektFilter) return;
                const label = [e.objekt, e.kontocode].filter(Boolean).join(" · ");
                seen.set(e.iban, label ? `${label} · ${e.iban}` : e.iban);
              });
              return [...seen.entries()].map(([iban, label]) => (
                <option key={iban} value={iban}>{label}</option>
              ));
            })()}
          </select>

          <input
            type="text"
            placeholder="Filtern nach Begünstigter oder KurzName"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {strgView === "luecken" ? (
          <LueckenAnsicht
            objektFilter={objektFilter}
            selectedIban={selectedIban}
            textFilter={filter}
            vorschlaege={strgVorschlaege}
            onRegelAngelegt={fetchEntries}
          />
        ) : (
        <>
        <table className="strg-table">
          <thead>
            <tr>
              <th>Begünstigter</th>
              <th>Empfänger-IBAN</th>
              <th>KurzName</th>
              <th>Kategorie</th>
              <th>SubKat</th>
              <th>Kst</th>
              <th>Mwst</th>
              <th>Umlageart</th>
              <th>BelegID</th>
              <th>Gegenkonto</th>
              <th className="col-actions">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {angezeigteEntries.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: "center", opacity: 0.7, padding: 16 }}>
                {entries.length === 0 ? "Keine Regeln geladen." : "Keine Treffer im aktuellen Filter."}
              </td></tr>
            )}
            {angezeigteEntries.map((entry) => (
              <tr key={entry.id}>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Beguenstigter ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Beguenstigter: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Beguenstigter
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.ZahlungsempfängerIBAN ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, ZahlungsempfängerIBAN: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.ZahlungsempfängerIBAN
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.KurzName ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, KurzName: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.KurzName
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Kategorie ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Kategorie: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Kategorie
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.SubKat ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, SubKat: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.SubKat
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Kst ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Kst: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Kst
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Mwst ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Mwst: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Mwst
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Umlageart ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Umlageart: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Umlageart
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.BelegID ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, BelegID: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.BelegID
                  )}
                </td>
                <td>
                  {editIndex === entry.id ? (
                    <input
                      style={{ width: "100%" }}
                      value={entry.Gegenkonto ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEntries((prev) =>
                          prev.map((item) => item.id === entry.id ? { ...item, Gegenkonto: val } : item)
                        );
                      }}
                    />
                  ) : (
                    entry.Gegenkonto
                  )}
                </td>
                <td className="col-actions">
                  {editIndex === entry.id ? (
                    <div className="btn-group">
                      <button
                        className="icon-btn success"
                        title="Speichern"
                        onClick={() => handleUpdate(entry.id)}
                      >
                        <FaCheck />
                      </button>
                      <button
                        className="icon-btn danger"
                        title="Abbrechen"
                        onClick={() => setEditIndex(null)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <div className="btn-group">
                      <button
                        className="icon-btn"
                        title="Bearbeiten"
                        onClick={() => setEditIndex(entry.id)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="icon-btn danger"
                        title="Löschen"
                        onClick={() => handleDelete(entry.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {showNewForm && (
          <div className="card">
            <h3>Neuen Eintrag erstellen</h3>
            <div className="grid-form">
              {[["iban", "Konto-IBAN (Quellkonto) *"],
                ["ZahlungsempfängerIBAN", "Partner-IBAN (Zahlungsempfänger) *"],
                ["Beguenstigter", "Begünstigter (Name)"],
                ["KurzName", "KurzName"],
                ["Kategorie", "Kategorie"],
                ["SubKat", "SubKat"],
                ["Kst", "Kostenstelle"],
                ["Mwst", "MwSt (19 oder 0,19)"],
                ["Umlageart", "Umlageart"],
                ["BelegID", "BelegID"],
                ["Gegenkonto", "Gegenkonto"]].map(([key, label]) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", fontSize: 11 }}>
                  {label}
                  <input
                    placeholder={label}
                    value={newEntry[key] ?? ""}
                    onChange={(e) => setNewEntry({ ...newEntry, [key]: e.target.value })}
                  />
                </label>
              ))}
            </div>
            <button onClick={handleCreate}>Eintrag speichern</button>
          </div>
        )}
        <button onClick={() => {
          if (!showNewForm) {
            // Welle 46: Quellkonto aus der Auswahl vorbefüllen
            setNewEntry((n) => ({ ...n, iban: n.iban || selectedIban }));
          }
          setShowNewForm(!showNewForm);
        }}>
          {showNewForm ? "Erstellen schließen" : "Neuen Eintrag erstellen"}
        </button>
        </>
        )}
      </>
    )}
  </div>
);
};

export default StrgPage;
